import React, { useState, useRef, useEffect, useCallback, useContext, useMemo } from "react";
import ReactDOM from "react-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion } from "framer-motion";
import friendshipService from "../../../services/friendshipService";
import "./index.scss";
import PopupOverlay from "../../organisms/Background/PopupOverlay";
import ChatComposer from "../../molecules/ChatComposer";
import ChatMessages from "../../organisms/Chat/ChatMessages";
import ChatConversationHeader from "../../organisms/Chat/ChatConversationHeader";
import BackButton from "../../atoms/BackButton";
import CloseButton from "../../atoms/CloseButton";
import { MobileHeaderSlotContext } from "../../organisms/MainPage/MobileNavigation/MobileHeaderSlotContext";
import ChatList from "../../organisms/Chat/ChatList";
import FriendSearchDropdown from "../../molecules/FriendSearchDropdown";
import DeleteChatPopup from "../../molecules/DeleteChatPopup";
import { useUnreadMessages } from "../../../store/UnreadMessagesContext";
import { usePresence } from "../../../store/PresenceContext";
import { useStompClient } from "../../../store/StompContext";
import { fetchPresenceStatuses } from "../../../services/presenceService";
import FrostedPanel from "../../molecules/FrostedPanel";
import classNames from "../../../utils/classNames";
import {
    normalizeChatMessage,
    normalizeMessageReactions,
    mergeMessageReactions,
    upsertMessage,
} from "../../../utils/messageUtils";
import UserCardAvatar from "../../molecules/UserCardAvatar";
import chatService from "../../../services/chatService";
import { uploadChatAttachment } from "../../../services/mediaService";
import { buildAuthHeaders } from "../../../services/authTokenStore";
import { resolveGatewayBackedServiceBaseUrl } from "../../../services/serviceBaseUrl";
import { hasVisibleContent, sanitizeHtml } from "../../molecules/Composer/sanitize";
import { useMediaQuery } from "../../../hooks/useMediaQuery";
import { MOBILE_MAX_WIDTH, mediaQueryMaxWidth } from "../../../constants/breakpoints";
import {
    CHAT_SEARCH_MAX_PAGES,
    CHAT_SEARCH_PAGE_SIZE,
    CHAT_SEARCH_RESULT_LIMIT,
    DEFAULT_CHAT_MESSAGE_PAGE_SIZE,
} from "../../../constants/chat";
import { CHAT_TAB_ID } from "../../../constants/MainNavigationTabSpecs";
import { NAVIGATE_TAB_EVENT } from "../../../constants/navigation";
import chatBackIcon from "../../../images/chat-back-arrow.svg";
import { ReactComponent as ChatFilterUnreadIcon } from "../../../images/chat-filter-unread.svg";
import { ReactComponent as ChatFilterPinnedIcon } from "../../../images/chat-filter-pinned.svg";
import { ReactComponent as ChatFilterMutedIcon } from "../../../images/chat-filter-muted.svg";
import { ReactComponent as MessageIcon } from "../../../images/message-icon.svg";
import InfoIcon from "../../atoms/InfoIcon";
import LoadingSpinner from "../../atoms/LoadingSpinner";
import IconButton from "../../atoms/IconButton";
import ScrollArea from "../../atoms/ScrollArea";
import {
    buildSelfDisplayName,
    normalizeId,
    normalizeParticipantIds,
    resolveDirectChatIdentity,
} from "../../../utils/directChatIdentity";
import { consumeChatNavigationIntent } from "../../../utils/chatNavigationIntent";
import { matchesUserIdentityQuery, resolveUserDisplayName, resolveUserUsername } from "../../../utils/userIdentity";
import {
    GROUP_CHAT_CONTEXT_TYPE,
    isGroupChatConversation,
    resolveChatAvatarAlt,
    resolveChatAvatarProps,
} from "../../../utils/chatAvatar";
import { resolveChatMessageAuthorName } from "../../../utils/chatMessageAuthor";
import {
    normalizeSelectedChatId,
    persistSelectedChatSelection,
    readSelectedChatSelection,
} from "../../../utils/chatSelectedChatStorage";

function ChatComposerAccessory({
    replyingTo,
    onCancelReply,
    editingMessage,
    onCancelEdit,
    resolveUserName,
    currentUserId,
    currentUser,
}) {
    const { t } = useTranslation();
    const activeMessage = editingMessage || replyingTo;
    if (!activeMessage) {
        return null;
    }

    const label = editingMessage
        ? t("chatPage.editingMessageLabel")
        : t("chatPage.replyingToLabel");
    const author = resolveChatMessageAuthorName({
        message: activeMessage,
        resolveUserName,
        currentUserId,
        currentUser,
        fallback: t("chatPage.anonymousUser"),
    });
    const preview = toPlainText(activeMessage?.content || activeMessage?.text || "");
    const handleCancel = editingMessage ? onCancelEdit : onCancelReply;

    return (
        <div className="chat-composer-accessory" role="status" aria-live="polite">
            <div className="chat-composer-accessory__content">
                <span className="chat-composer-accessory__heading">
                    <span className="chat-composer-accessory__label">{label}</span>
                    <strong className="chat-composer-accessory__author">{author}</strong>
                </span>
                <p className="chat-composer-accessory__preview">
                    {preview || t("chatPage.emptyMessagePlaceholder")}
                </p>
            </div>
            <CloseButton
                className="chat-composer-accessory__cancel close-button--ghost"
                ariaLabel={t("chatPage.dismissDraft")}
                onClick={handleCancel}
            />
        </div>
    );
}

const API_URL = resolveGatewayBackedServiceBaseUrl(process.env.REACT_APP_USER_SERVICE_URL);
const CHAT_SERVICE_URL = resolveGatewayBackedServiceBaseUrl(process.env.REACT_APP_CHAT_SERVICE_URL);
const PINNED_STORAGE_KEY = "muzone.chat.pinned";
const MUTED_STORAGE_KEY = "muzone.chat.muted";
const MESSAGE_PAGE_SIZE = DEFAULT_CHAT_MESSAGE_PAGE_SIZE;
const GROUP_CHAT_TOTAL_PARTICIPANT_LIMIT = 10;
const GROUP_CHAT_SELECTED_USER_LIMIT = GROUP_CHAT_TOTAL_PARTICIPANT_LIMIT - 1;
const GROUP_CHAT_MIN_SELECTED_USERS = 2;
/* A chat with nothing to preview carries no preview at all. The placeholder is
   applied on the way to the list, so no real message text can be mistaken for
   the empty state — a message that literally reads "No messages yet" is still
   that member's message. */
const NO_CHAT_PREVIEW = null;

export const applyEmptyPreviewLabel = (chats, label) => chats.map((chat) => (
    chat.latestMessage ? chat : { ...chat, latestMessage: label }
));

const CHAT_CONTEXT_TYPES = Object.freeze({
    DIRECT: "DIRECT",
    GROUP: GROUP_CHAT_CONTEXT_TYPE,
});

const isDirectChatContext = (chat) => chat?.contextType === CHAT_CONTEXT_TYPES.DIRECT;
const isGroupChatContext = isGroupChatConversation;

const unsubscribeSubscriptions = (subscriptions) => {
    Object.values(subscriptions || {}).forEach((subscription) => {
        try {
            subscription?.unsubscribe?.();
        } catch (_) {
            // ignore cleanup errors
        }
    });
};

const mergeChatSummaryPreservingLoadedPreview = (existingChat, nextChat) => {
    if (!existingChat) {
        return nextChat;
    }
    return {
        ...existingChat,
        ...nextChat,
        latestMessage: existingChat.latestMessage ?? nextChat.latestMessage,
        lastMessageTimestamp: existingChat.lastMessageTimestamp ?? nextChat.lastMessageTimestamp,
        unreadMessages: existingChat.unreadMessages ?? nextChat.unreadMessages,
    };
};

const toMillis = (value) => {
    if (!value) {
        return 0;
    }
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? 0 : parsed;
};

const clampSearchQuery = (value = "") => value.replace(/\s+/g, " ").trim().slice(0, 160);

const toPlainText = (html) => {
    if (!html) {
        return "";
    }
    const raw = String(html);
    if (typeof window !== "undefined" && typeof document !== "undefined") {
        const container = document.createElement("div");
        container.innerHTML = raw;
        return (container.textContent || "").replace(/\s+/g, " ").trim();
    }
    return raw.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
};

// Remove any local blob previews that were not uploaded successfully.
const stripBlobImgTags = (html) => {
    try {
        if (!html || typeof html !== "string" || html.indexOf("blob:") === -1) {
            return html;
        }
        if (typeof document === "undefined") {
            return html.replace(/<img[^>]*src\s*=\s*['"]blob:[^'"<>]+['"][^>]*>/gi, "");
        }
        const container = document.createElement("div");
        container.innerHTML = html;
        container.querySelectorAll('img[src^="blob:"]').forEach((img) => img.remove());
        return container.innerHTML;
    } catch (_) {
        return html;
    }
};

const getBlobImageSrcSet = (html) => {
    try {
        const urls = new Set();
        if (!html || typeof html !== "string" || html.indexOf("blob:") === -1) {
            return urls;
        }
        if (typeof document === "undefined") {
            const pattern = /<img[^>]*src\s*=\s*['"](blob:[^'"<>]+)['"][^>]*>/gi;
            let match = pattern.exec(html);
            while (match) {
                if (match[1]) {
                    urls.add(String(match[1]));
                }
                match = pattern.exec(html);
            }
            return urls;
        }
        const container = document.createElement("div");
        container.innerHTML = html;
        container.querySelectorAll('img[src^="blob:"]').forEach((img) => {
            const src = img.getAttribute("src");
            if (src) {
                urls.add(String(src));
            }
        });
        return urls;
    } catch (_) {
        return new Set();
    }
};

const buildSnippet = (text, needle, maxLength = 120) => {
    if (!text) {
        return "";
    }
    const lowerText = text.toLowerCase();
    const index = needle ? lowerText.indexOf(needle.toLowerCase()) : -1;
    if (index === -1) {
        return text.slice(0, maxLength) + (text.length > maxLength ? "…" : "");
    }
    const start = Math.max(0, index - Math.floor(maxLength / 3));
    const end = Math.min(text.length, index + needle.length + Math.floor(maxLength / 2));
    const prefix = start > 0 ? "…" : "";
    const suffix = end < text.length ? "…" : "";
    return `${prefix}${text.slice(start, end).trim()}${suffix}`;
};

export function ChatPage() {
    const [messages, setMessages] = useState([]);
    const [messageTotals, setMessageTotals] = useState({});
    const [chats, setChats] = useState([]);
    const [chatsLoading, setChatsLoading] = useState(true);
    const [friends, setFriends] = useState([]);
    const [filteredFriends, setFilteredFriends] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [showDropdown, setShowDropdown] = useState(false);
    const [newChatMode, setNewChatMode] = useState("direct");
    const [selectedGroupUserIds, setSelectedGroupUserIds] = useState(() => new Set());
    const [groupChatCreateBusy, setGroupChatCreateBusy] = useState(false);
    const [groupChatCreateError, setGroupChatCreateError] = useState(null);
    const [selectedChatId, setSelectedChatId] = useState(null);
    const [chatToDelete, setChatToDelete] = useState(null);
    const [currentUser, setCurrentUser] = useState(null);
    const [currentUserId, setCurrentUserId] = useState(null);
    const [threadQuery, setThreadQuery] = useState("");
    const [chatFilter, setChatFilter] = useState("all");
    const [pinnedChatIds, setPinnedChatIds] = useState(() => new Set());
    const [mutedChatIds, setMutedChatIds] = useState(() => new Set());
    const [hasMoreMessages, setHasMoreMessages] = useState(false);
    const [messageCursor, setMessageCursor] = useState(null);
    const [messagesLoading, setMessagesLoading] = useState(false);
    const [olderMessagesLoading, setOlderMessagesLoading] = useState(false);
    const [mobilePanel, setMobilePanel] = useState("threads");
    const [replyingTo, setReplyingTo] = useState(null);
    const [editingMessage, setEditingMessage] = useState(null);
    const [chatDraftHtml, setChatDraftHtml] = useState("");
    const [composerSendingChatIds, setComposerSendingChatIds] = useState(() => new Set());
    const [composerError, setComposerError] = useState(null);
    const [chatSearchOpen, setChatSearchOpen] = useState(false);
    const [chatSearchQuery, setChatSearchQuery] = useState("");
    const [chatSearchResults, setChatSearchResults] = useState([]);
    const [chatSearchLoading, setChatSearchLoading] = useState(false);
    const [chatSearchNavigating, setChatSearchNavigating] = useState(false);
    const [chatSearchError, setChatSearchError] = useState(null);
    const [chatSearchHasMore, setChatSearchHasMore] = useState(false);
    const [showPinnedHint, setShowPinnedHint] = useState(false);
    const hiddenMessagesRef = useRef(new Map());
    const chatSearchInputRef = useRef(null);
    const [pinnedMessageIds, setPinnedMessageIds] = useState(() => new Set());
    const [downloadingAttachments, setDownloadingAttachments] = useState(() => new Set());
    const { setTotalUnread } = useUnreadMessages();
    const { upsertMany, getStatus, trackUsers } = usePresence();
    const isMobileViewport = useMediaQuery(mediaQueryMaxWidth(MOBILE_MAX_WIDTH));
    const mobileHeaderSlot = useContext(MobileHeaderSlotContext);
    const { t, i18n } = useTranslation();
    const { client: stompClient, isConnected: isSocketConnected } = useStompClient();
    const currentUserIdString = useMemo(() => normalizeId(currentUserId), [currentUserId]);
    const currentUserIdStringRef = useRef(currentUserIdString);
    const selfDisplayName = useMemo(
        () => buildSelfDisplayName({
            username: currentUser?.username,
            displayName: currentUser?.displayName,
            email: currentUser?.email,
        }),
        [currentUser?.username, currentUser?.displayName, currentUser?.email]
    );

    const subscriptionsRef = useRef({});

    // Keep a ref of the latest selectedChatId
    const selectedChatIdRef = useRef(null);
    const initialSelectedChatIdRef = useRef(null);
    const initialSelectionSettledRef = useRef(false);
    const chatSearchRequestRef = useRef({ id: 0 });
    const chatSearchNavigateRef = useRef({ id: 0 });
    useEffect(() => {
        const pendingIntent = normalizeSelectedChatId(consumeChatNavigationIntent());
        if (pendingIntent) {
            initialSelectedChatIdRef.current = pendingIntent;
            initialSelectionSettledRef.current = false;
            return;
        }
        const storedSelection = readSelectedChatSelection();
        initialSelectedChatIdRef.current = storedSelection.chatId;
        initialSelectionSettledRef.current = !storedSelection.chatId;
    }, []);
    useEffect(() => {
        selectedChatIdRef.current = selectedChatId;
    }, [selectedChatId]);

    useEffect(() => {
        if (!initialSelectionSettledRef.current && !selectedChatId) {
            return;
        }
        persistSelectedChatSelection(selectedChatId);
    }, [selectedChatId]);

    const isComposerSending = useMemo(() => {
        if (selectedChatId === undefined || selectedChatId === null) {
            return false;
        }
        return composerSendingChatIds.has(String(selectedChatId));
    }, [composerSendingChatIds, selectedChatId]);

    const setComposerSendingForChat = useCallback((chatId, sending) => {
        if (chatId === undefined || chatId === null) {
            return;
        }
        const key = String(chatId);
        setComposerSendingChatIds((prev) => {
            const hasKey = prev.has(key);
            if ((sending && hasKey) || (!sending && !hasKey)) {
                return prev;
            }
            const next = new Set(prev);
            if (sending) {
                next.add(key);
            } else {
                next.delete(key);
            }
            return next;
        });
    }, []);

    useEffect(() => {
        setChatSearchOpen(false);
        setChatSearchQuery("");
        setChatSearchResults([]);
        setChatSearchError(null);
        setChatSearchHasMore(false);
        setChatSearchLoading(false);
        setChatSearchNavigating(false);
        setShowPinnedHint(false);
        setChatDraftHtml("");
        setComposerError(null);
    }, [selectedChatId]);

    useEffect(() => {
        chatsRef.current = chats;
    }, [chats]);

    useEffect(() => {
        friendsRef.current = friends;
    }, [friends]);

    const trackedPresenceUserIds = useMemo(() => {
        const userIds = new Set();
        chats.forEach((chat) => {
            if (!isDirectChatContext(chat)) {
                return;
            }
            const identityUserId = Array.isArray(chat?.participantIds)
                ? resolveDirectChatIdentity({
                    participantIds: chat.participantIds,
                    currentUserId: currentUserIdString,
                }).userId
                : null;
            const userId = normalizeId(chat?.avatarUserId) || normalizeId(identityUserId);
            if (userId) {
                userIds.add(userId);
            }
        });
        friends.forEach((friend) => {
            const userId = normalizeId(friend?.userId ?? friend?.id);
            if (userId) {
                userIds.add(userId);
            }
        });
        return Array.from(userIds);
    }, [chats, currentUserIdString, friends]);

    useEffect(() => {
        if (trackedPresenceUserIds.length === 0) {
            return undefined;
        }
        return trackUsers(trackedPresenceUserIds);
    }, [trackUsers, trackedPresenceUserIds]);

    useEffect(() => {
        messagesRef.current = messages;
    }, [messages]);

    useEffect(() => {
        currentUserIdStringRef.current = currentUserIdString;
    }, [currentUserIdString]);

    const hiddenStorageKey = useMemo(() => {
        if (currentUserId === undefined || currentUserId === null) {
            return null;
        }
        return `muzone.chat.hidden.${currentUserId}`;
    }, [currentUserId]);

    const filterHiddenMessages = useCallback((messageList, chatIdOverride = null) => {
        if (!Array.isArray(messageList) || !currentUserId) {
            return messageList;
        }
        const hiddenMap = hiddenMessagesRef.current;
        if (!hiddenMap || hiddenMap.size === 0) {
            return messageList;
        }
        return messageList.filter((message) => {
            const chatKey = chatIdOverride
                ? String(chatIdOverride)
                : (message?.chatId ? String(message.chatId) : null);
            if (!chatKey) {
                return true;
            }
            const hiddenSet = hiddenMap.get(chatKey);
            if (!hiddenSet) {
                return true;
            }
            return !hiddenSet.has(String(message.id));
        });
    }, [currentUserId]);

    const persistHiddenMessages = useCallback(() => {
        if (!hiddenStorageKey) {
            return;
        }
        const payload = {};
        hiddenMessagesRef.current.forEach((set, chatId) => {
            if (set && set.size > 0) {
                payload[chatId] = Array.from(set);
            }
        });
        try {
            window.localStorage.setItem(hiddenStorageKey, JSON.stringify(payload));
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error("Failed to persist hidden messages", error);
        }
    }, [hiddenStorageKey]);

    useEffect(() => {
        if (!hiddenStorageKey) {
            hiddenMessagesRef.current = new Map();
            return;
        }
        try {
            const raw = window.localStorage.getItem(hiddenStorageKey);
            if (!raw) {
                hiddenMessagesRef.current = new Map();
                return;
            }
            const parsed = JSON.parse(raw);
            const map = new Map();
            if (parsed && typeof parsed === "object") {
                Object.entries(parsed).forEach(([chatId, ids]) => {
                    const set = new Set(
                        Array.isArray(ids)
                            ? ids.map((id) => String(id))
                            : []
                    );
                    if (set.size > 0) {
                        map.set(String(chatId), set);
                    }
                });
            }
            hiddenMessagesRef.current = map;
            setMessages((prev) => filterHiddenMessages(prev));
        } catch (error) {
            hiddenMessagesRef.current = new Map();
        }
    }, [filterHiddenMessages, hiddenStorageKey]);

    useEffect(() => {
        if (!isMobileViewport) {
            return;
        }
        setMobilePanel((prev) => {
            if (!selectedChatId && prev !== "threads") {
                return "threads";
            }
            if (selectedChatId && prev !== "conversation") {
                return "conversation";
            }
            return prev;
        });
    }, [isMobileViewport, selectedChatId]);

    useEffect(() => {
        setReplyingTo(null);
        setEditingMessage(null);
    }, [selectedChatId]);

    useEffect(() => {
        if (!Array.isArray(chats)) {
            return;
        }
        const validIds = new Set(chats.map((chat) => String(chat.id)));

        setPinnedChatIds((prev) => {
            let mutated = false;
            const next = new Set();
            prev.forEach((id) => {
                if (validIds.has(id)) {
                    next.add(id);
                } else {
                    mutated = true;
                }
            });
            return mutated ? next : prev;
        });

        setMutedChatIds((prev) => {
            let mutated = false;
            const next = new Set();
            prev.forEach((id) => {
                if (validIds.has(id)) {
                    next.add(id);
                } else {
                    mutated = true;
                }
            });
            return mutated ? next : prev;
        });
    }, [chats]);


    const dropdownRef = useRef(null);
    const addButtonRef = useRef(null);
    const chatRefs = useRef({});
    const chatsRef = useRef([]);
    const friendsRef = useRef([]);
    const chatMessagesRef = useRef(null);
    const messagesRef = useRef([]);
    const pendingDeletedMessageIdsRef = useRef(new Set());
    const messagesLoadingChatIdRef = useRef(null);
    const olderMessagesLoadingChatIdRef = useRef(null);
    const userDirectoryRef = useRef(new Map());
    const typingTimeoutsRef = useRef(new Map());
    const typingPublishRef = useRef(new Map());
    const [typingIndicators, setTypingIndicators] = useState({});
    const previousChatIdRef = useRef(null);

    useEffect(() => {
        if (typeof window === "undefined") {
            return;
        }

        try {
            const pinnedRaw = window.localStorage.getItem(PINNED_STORAGE_KEY);
            if (pinnedRaw) {
                const parsed = JSON.parse(pinnedRaw);
                if (Array.isArray(parsed)) {
                    setPinnedChatIds(() => new Set(parsed.map(String)));
                }
            }

            const mutedRaw = window.localStorage.getItem(MUTED_STORAGE_KEY);
            if (mutedRaw) {
                const parsedMuted = JSON.parse(mutedRaw);
                if (Array.isArray(parsedMuted)) {
                    setMutedChatIds(() => new Set(parsedMuted.map(String)));
                }
            }
        } catch (error) {
            console.warn("Unable to restore chat preferences", error);
        }
    }, []);

    useEffect(() => {
        if (typeof window === "undefined") {
            return;
        }
        window.localStorage.setItem(
            PINNED_STORAGE_KEY,
            JSON.stringify(Array.from(pinnedChatIds))
        );
    }, [pinnedChatIds]);

    useEffect(() => {
        if (typeof window === "undefined") {
            return;
        }
        window.localStorage.setItem(
            MUTED_STORAGE_KEY,
            JSON.stringify(Array.from(mutedChatIds))
        );
    }, [mutedChatIds]);

    // 1) Fetch current user once on mount
    useEffect(() => {
        const fetchCurrentUser = async () => {
            try {
                const response = await axios.get(`${API_URL}/api/users/me`, {
                    withCredentials: true,
                });
                const nextUser = response?.data && typeof response.data === "object"
                    ? response.data
                    : null;
                setCurrentUser(nextUser);
                setCurrentUserId(nextUser?.id ?? null);
            } catch (error) {
                console.error("Error fetching current user:", error);
                setCurrentUser(null);
                setCurrentUserId(null);
            }
        };
        fetchCurrentUser();
    }, []);

    const normalizeMessage = useCallback(
        (message) =>
            normalizeChatMessage(message, {
                ensureSeenByArray: true,
            }),
        []
    );

    const setMessageTotal = useCallback((chatId, total) => {
        if (!chatId) {
            return;
        }
        setMessageTotals((prev) => {
            const key = String(chatId);
            const numericTotal = Number(total);
            if (!Number.isFinite(numericTotal)) {
                if (!Object.prototype.hasOwnProperty.call(prev, key)) {
                    return prev;
                }
                const next = { ...prev };
                delete next[key];
                return next;
            }
            const normalizedCount = Math.max(0, Math.trunc(numericTotal));
            if (prev[key] === normalizedCount) {
                return prev;
            }
            return { ...prev, [key]: normalizedCount };
        });
    }, []);

    const adjustMessageTotal = useCallback((chatId, delta) => {
        if (!chatId || !Number.isFinite(delta) || delta === 0) {
            return;
        }
        setMessageTotals((prev) => {
            const key = String(chatId);
            const previousValue = prev[key];
            const fallbackLength = Array.isArray(messagesRef.current)
                ? messagesRef.current.length
                : 0;
            const baseline = Number.isFinite(previousValue)
                ? previousValue
                : Math.max(0, Math.trunc(fallbackLength));
            const nextValue = Math.max(0, Math.trunc(baseline + delta));
            if (nextValue === baseline) {
                return prev;
            }
            return { ...prev, [key]: nextValue };
        });
    }, [messagesRef]);

    const fetchLatestVisiblePreview = useCallback(async (chatId) => {
        const fallback = { latestMessage: NO_CHAT_PREVIEW, lastMessageTimestamp: null };
        if (!chatId) {
            return fallback;
        }
        const key = String(chatId);
        const hiddenSet = hiddenMessagesRef.current.get(key);
        const toPreview = (message) => {
            if (!message) {
                return null;
            }
            const normalized = normalizeMessage(message);
            if (hiddenSet && normalized?.id && hiddenSet.has(String(normalized.id))) {
                return null;
            }
            return {
                latestMessage: normalized?.content ?? NO_CHAT_PREVIEW,
                lastMessageTimestamp:
                    normalized?.timestamp
                    ?? normalized?.createdAt
                    ?? normalized?.sentAt
                    ?? null,
            };
        };

        try {
            const latest = await chatService.getLatestMessage(chatId);
            const preview = toPreview(latest);
            if (preview) {
                return preview;
            }
        } catch (error) {
            console.error(`Failed to fetch latest visible message for chat ${chatId}`, error);
        }

        try {
            const response = await chatService.getChatMessagesPage(chatId, { limit: MESSAGE_PAGE_SIZE });
            const items = Array.isArray(response?.items) ? response.items : [];
            const normalized = items.map(normalizeMessage);
            const visibleMessages = filterHiddenMessages(normalized, chatId);
            const lastVisible = visibleMessages.length > 0
                ? visibleMessages[visibleMessages.length - 1]
                : null;
            const preview = toPreview(lastVisible);
            if (preview) {
                return preview;
            }
        } catch (error) {
            console.error(`Failed to resolve visible message history for chat ${chatId}`, error);
        }

        return fallback;
    }, [filterHiddenMessages, normalizeMessage]);

    const fetchChatUnreadCount = useCallback(async (chatId) => {
        if (!chatId || !currentUserIdString) {
            return 0;
        }
        try {
            const unreadRes = await axios.get(
                `${CHAT_SERVICE_URL}/api/chat/${encodeURIComponent(chatId)}/unread-count/${encodeURIComponent(currentUserIdString)}`,
                { withCredentials: true }
            );
            const count = Number(unreadRes.data);
            return Number.isFinite(count) ? Math.max(0, Math.trunc(count)) : 0;
        } catch (err) {
            console.error(`Error fetching unread count for chat ${chatId}:`, err);
            return 0;
        }
    }, [currentUserIdString]);

    const updateChatPreviewAfterHide = useCallback((chatId) => {
        if (!chatId) {
            return;
        }
        const key = String(chatId);
        const hiddenSet = hiddenMessagesRef.current.get(key) || new Set();
        const visibleMessages = Array.isArray(messagesRef.current)
            ? messagesRef.current.filter(
                (message) => String(message.chatId) === key && !hiddenSet.has(String(message.id))
            )
            : [];
        const hasVisible = visibleMessages.length > 0;
        const latestVisible = hasVisible ? visibleMessages[visibleMessages.length - 1] : null;
        setChats((prevChats) =>
            prevChats.map((chat) => {
                if (String(chat.id) !== key) {
                    return chat;
                }
                if (!hasVisible) {
                    return {
                        ...chat,
                        latestMessage: NO_CHAT_PREVIEW,
                        lastMessageTimestamp: null,
                    };
                }
                return {
                    ...chat,
                    latestMessage: latestVisible.content || chat.latestMessage,
                    lastMessageTimestamp: latestVisible.timestamp
                        ?? latestVisible.createdAt
                        ?? latestVisible.sentAt
                        ?? chat.lastMessageTimestamp,
                };
            })
        );
    }, []);

    const hideMessageForCurrentUser = useCallback((message) => {
        if (!message || !message.id) {
            return;
        }
        const chatKey = message.chatId
            ? String(message.chatId)
            : (selectedChatIdRef.current ? String(selectedChatIdRef.current) : null);
        if (!chatKey) {
            return;
        }
        const idKey = String(message.id);
        const map = hiddenMessagesRef.current || new Map();
        const set = map.get(chatKey) || new Set();
        if (!set.has(idKey)) {
            set.add(idKey);
            map.set(chatKey, set);
            hiddenMessagesRef.current = map;
            persistHiddenMessages();
        }
        setMessages((prev) => prev.filter((m) => String(m.id) !== idKey));
        updateChatPreviewAfterHide(chatKey);
    }, [persistHiddenMessages, updateChatPreviewAfterHide]);

    const untrackHiddenMessage = useCallback((messageId, chatId) => {
        if (!messageId || !chatId) {
            return;
        }
        const key = String(chatId);
        const idKey = String(messageId);
        const map = hiddenMessagesRef.current;
        if (!map || !map.has(key)) {
            return;
        }
        const set = map.get(key);
        if (!set.has(idKey)) {
            return;
        }
        set.delete(idKey);
        if (set.size === 0) {
            map.delete(key);
        } else {
            map.set(key, set);
        }
        hiddenMessagesRef.current = map;
        persistHiddenMessages();
    }, [persistHiddenMessages]);

    const refreshMessageTotal = useCallback(async (chatId) => {
        if (!chatId) {
            return;
        }
        try {
            const total = await chatService.getChatMessageCount(chatId);
            setMessageTotal(chatId, total);
        } catch (error) {
            console.error("Failed to refresh chat message count", error);
        }
    }, [setMessageTotal]);

    const selectChat = useCallback((chatId) => {
        const normalizedChatId = normalizeSelectedChatId(chatId);
        initialSelectedChatIdRef.current = null;
        initialSelectionSettledRef.current = true;
        setSelectedChatId(normalizedChatId);
        if (isMobileViewport) {
            setMobilePanel(normalizedChatId ? "conversation" : "threads");
        }
    }, [isMobileViewport]);

    useEffect(() => {
        const pendingChatId = initialSelectedChatIdRef.current;
        if (!pendingChatId) {
            initialSelectionSettledRef.current = true;
            return;
        }
        const hasMatch = chats.some((chat) => String(chat.id) === pendingChatId);
        if (!hasMatch) {
            if (!chatsLoading) {
                initialSelectedChatIdRef.current = null;
                initialSelectionSettledRef.current = true;
                if (!selectedChatIdRef.current) {
                    persistSelectedChatSelection(null);
                }
            }
            return;
        }
        if (String(selectedChatIdRef.current ?? "") !== pendingChatId) {
            selectChat(pendingChatId);
        } else {
            initialSelectedChatIdRef.current = null;
            initialSelectionSettledRef.current = true;
        }
    }, [chats, chatsLoading, selectChat]);

    const handleMobileBack = useCallback(() => {
        selectChat(null);
    }, [selectChat]);

    const loadLatestMessages = useCallback(async (chatId, options = {}) => {
        if (!chatId) {
            return;
        }
        const { ignoreStale = true } = options;
        messagesLoadingChatIdRef.current = chatId;
        setMessagesLoading(true);
        try {
            const response = await chatService.getChatMessagesPage(chatId, { limit: MESSAGE_PAGE_SIZE });
            const items = Array.isArray(response?.items) ? response.items : [];
            const normalized = items.map((msg) => normalizeMessage(msg));
            const hasMore = Boolean(response?.hasMore) && Boolean(response?.nextCursor);
            const nextCursor = hasMore ? response.nextCursor : null;
            const serverTotalRaw = response?.totalCount;
            const numericServerTotal = Number(serverTotalRaw);
            const totalFromResponse = Number.isFinite(numericServerTotal)
                ? numericServerTotal
                : normalized.length;

            if (ignoreStale && String(selectedChatIdRef.current ?? "") !== String(chatId)) {
                return;
            }

            setMessageTotal(chatId, totalFromResponse);
            if (!Number.isFinite(numericServerTotal)) {
                refreshMessageTotal(chatId);
            }
            const visibleMessages = filterHiddenMessages(normalized, chatId);
            setMessages(visibleMessages);
            setHasMoreMessages(hasMore);
            setMessageCursor(nextCursor);
            if (typeof window !== "undefined") {
                window.requestAnimationFrame(() => {
                    chatMessagesRef.current?.scrollToBottom({ behavior: "auto", settle: true });
                });
            } else if (chatMessagesRef.current?.scrollToBottom) {
                chatMessagesRef.current.scrollToBottom({ behavior: "auto", settle: true });
            }
        } catch (error) {
            console.error("❌ Error fetching messages:", error);
            if (!ignoreStale || String(selectedChatIdRef.current ?? "") === String(chatId)) {
                setMessages([]);
                setHasMoreMessages(false);
                setMessageCursor(null);
                setMessageTotal(chatId, null);
            }
        } finally {
            if (messagesLoadingChatIdRef.current === chatId) {
                setMessagesLoading(false);
                messagesLoadingChatIdRef.current = null;
            }
        }
    }, [normalizeMessage, chatMessagesRef, setMessageTotal, refreshMessageTotal, filterHiddenMessages]);

    const loadOlderMessages = useCallback(async () => {
        if (!selectedChatId || !hasMoreMessages || olderMessagesLoading) {
            return { ok: false };
        }

        const chatId = selectedChatId;
        olderMessagesLoadingChatIdRef.current = chatId;

        const cursor = messageCursor;
        if (!cursor) {
            setHasMoreMessages(false);
            setMessageCursor(null);
            olderMessagesLoadingChatIdRef.current = null;
            return { ok: false };
        }

        setOlderMessagesLoading(true);
        try {
            const response = await chatService.getChatMessagesPage(chatId, {
                limit: MESSAGE_PAGE_SIZE,
                cursor,
            });
            const items = Array.isArray(response?.items) ? response.items : [];
            const normalized = items.map((msg) => normalizeMessage(msg));
            const filteredNormalized = filterHiddenMessages(normalized, chatId);
            const serverTotalRaw = response?.totalCount;
            const numericServerTotal = Number(serverTotalRaw);

            if (String(selectedChatIdRef.current ?? "") !== String(chatId)) {
                return { ok: false };
            }

            let insertedCount = 0;
            const applyMessagesUpdate = () => {
                setMessages((prev) => {
                    if (!Array.isArray(prev) || prev.length === 0) {
                        insertedCount = filteredNormalized.length;
                        return filteredNormalized.length > 0 ? [...filteredNormalized] : prev;
                    }
                    const existingIds = new Set(prev.map((m) => String(m.id)));
                    const deduped = filteredNormalized.filter((msg) => !existingIds.has(String(msg.id)));
                    insertedCount = deduped.length;
                    if (deduped.length === 0) {
                        return prev;
                    }
                    return [...deduped, ...prev];
                });
            };

            if (filteredNormalized.length > 0 && chatMessagesRef.current?.preserveScroll) {
                chatMessagesRef.current.preserveScroll(applyMessagesUpdate);
            } else {
                applyMessagesUpdate();
            }

            const hasMore = Boolean(response?.hasMore) && Boolean(response?.nextCursor);
            const nextCursor = hasMore ? response.nextCursor : null;
            const madeProgress = !hasMore || String(nextCursor) !== String(cursor);
            setHasMoreMessages(hasMore);
            setMessageCursor(nextCursor);
            if (Number.isFinite(numericServerTotal)) {
                setMessageTotal(chatId, numericServerTotal);
            } else {
                refreshMessageTotal(chatId);
            }

            if (filteredNormalized.length === 0 || insertedCount === 0) {
                if (!Boolean(response?.hasMore)) {
                    setHasMoreMessages(false);
                    setMessageCursor(null);
                }
            }
            return { ok: madeProgress, hasMore };
        } catch (error) {
            console.error("❌ Error fetching older messages:", error);
            return { ok: false };
        } finally {
            if (olderMessagesLoadingChatIdRef.current === chatId) {
                setOlderMessagesLoading(false);
                olderMessagesLoadingChatIdRef.current = null;
            }
        }
    }, [
        selectedChatId,
        hasMoreMessages,
        olderMessagesLoading,
        messageCursor,
        normalizeMessage,
        chatMessagesRef,
        setMessageTotal,
        refreshMessageTotal,
        filterHiddenMessages,
    ]);

    const handleToggleChatSearch = useCallback(() => {
        setChatSearchOpen((prev) => !prev);
        setChatSearchError(null);
        setChatSearchResults([]);
        if (!chatSearchOpen) {
            if (typeof window !== "undefined" && typeof window.requestAnimationFrame === "function") {
                window.requestAnimationFrame(() => chatSearchInputRef.current?.focus());
            } else {
                chatSearchInputRef.current?.focus();
            }
        }
    }, [chatSearchOpen]);

    const handleChatSearchQueryChange = useCallback((event) => {
        setChatSearchQuery(event?.target?.value ?? "");
    }, []);

    const handleChatSearchClear = useCallback(() => {
        const nextRequestId = (chatSearchRequestRef.current?.id || 0) + 1;
        chatSearchRequestRef.current = {
            id: nextRequestId,
            chatId: selectedChatIdRef.current,
            query: "",
        };
        setChatSearchQuery("");
        setChatSearchResults([]);
        setChatSearchHasMore(false);
        setChatSearchError(null);
        setChatSearchLoading(false);
    }, []);

    const performChatSearch = useCallback(async () => {
        const chatId = selectedChatIdRef.current;
        if (!chatId) {
            return;
        }
        const normalizedQuery = clampSearchQuery(chatSearchQuery);
        setChatSearchQuery(normalizedQuery);
        const requestId = (chatSearchRequestRef.current?.id || 0) + 1;
        chatSearchRequestRef.current = { id: requestId, chatId, query: normalizedQuery };
        if (!normalizedQuery) {
            setChatSearchResults([]);
            setChatSearchHasMore(false);
            setChatSearchError(null);
            setChatSearchLoading(false);
            return;
        }
        setChatSearchLoading(true);
        setChatSearchError(null);
        const isStale = () => {
            const current = chatSearchRequestRef.current;
            return (
                !current
                || current.id !== requestId
                || String(current.chatId) !== String(chatId)
                || current.query !== normalizedQuery
                || String(selectedChatIdRef.current ?? "") !== String(chatId)
            );
        };
        try {
            const { results, hasMore } = await chatService.searchChatMessages(
                chatId,
                normalizedQuery,
                {
                    limit: CHAT_SEARCH_RESULT_LIMIT,
                    pageSize: CHAT_SEARCH_PAGE_SIZE,
                    maxPages: CHAT_SEARCH_MAX_PAGES,
                }
            );
            if (isStale()) {
                return;
            }
            const visibleResults = filterHiddenMessages(results.map(normalizeMessage), chatId);
            const dedupedIds = new Set();
            const withSnippets = visibleResults
                .filter((msg) => {
                    if (!msg || msg.id === undefined || msg.id === null) {
                        return false;
                    }
                    const key = String(msg.id);
                    if (dedupedIds.has(key)) {
                        return false;
                    }
                    dedupedIds.add(key);
                    return true;
                })
                .map((msg) => ({
                    message: msg,
                    snippet: buildSnippet(toPlainText(msg.content), normalizedQuery),
                }));
            setChatSearchResults(withSnippets);
            setChatSearchHasMore(hasMore);
        } catch (error) {
            console.error("Failed to search chat messages", error);
            if (isStale()) {
                return;
            }
            setChatSearchError(t("chatPage.search.error"));
        } finally {
            if (!isStale()) {
                setChatSearchLoading(false);
            }
        }
    }, [chatSearchQuery, filterHiddenMessages, normalizeMessage, t]);

    const handleChatSearchSubmit = useCallback((event) => {
        event?.preventDefault();
        performChatSearch();
    }, [performChatSearch]);

    const handleChatSearchResultSelect = useCallback(async (messageId) => {
        const chatId = selectedChatIdRef.current;
        if (!messageId || !chatId) {
            return;
        }
        const requestId = (chatSearchNavigateRef.current?.id || 0) + 1;
        chatSearchNavigateRef.current = { id: requestId, chatId, messageId };
        const isStale = () => {
            const current = chatSearchNavigateRef.current;
            return (
                !current
                || current.id !== requestId
                || String(current.chatId) !== String(chatId)
                || String(selectedChatIdRef.current ?? "") !== String(chatId)
            );
        };
        const key = String(messageId);
        const existing = Array.isArray(messagesRef.current)
            ? messagesRef.current.find((m) => m && String(m.id) === key)
            : null;
        if (existing) {
            chatMessagesRef.current?.scrollToMessage?.(messageId);
            setChatSearchError(null);
            setChatSearchOpen(false);
            return;
        }

        setChatSearchNavigating(true);
        try {
            let cursor = null;
            let hasMore = true;
            let pagesFetched = 0;
            let assembled = [];
            let found = null;
            while (hasMore && pagesFetched < CHAT_SEARCH_MAX_PAGES && !found) {
                // eslint-disable-next-line no-await-in-loop
                const response = await chatService.getChatMessagesPage(chatId, {
                    limit: MESSAGE_PAGE_SIZE,
                    cursor,
                });
                if (isStale()) {
                    return;
                }
                const items = Array.isArray(response?.items) ? response.items : [];
                const normalized = items.map(normalizeMessage);
                const filtered = filterHiddenMessages(normalized, chatId);
                assembled = assembled.length > 0 ? [...filtered, ...assembled] : filtered;
                found = filtered.find((msg) => msg && String(msg.id) === key) || null;
                hasMore = Boolean(response?.hasMore) && Boolean(response?.nextCursor);
                cursor = response?.nextCursor || null;
                pagesFetched += 1;
            }

            if (isStale()) {
                return;
            }

            if (found) {
                setMessages((prev) => {
                    const existing = Array.isArray(prev) ? prev : [];
                    const combined = [...existing, ...assembled];
                    const seen = new Set();
                    const deduped = [];
                    combined.forEach((msg) => {
                        if (!msg || msg.id === undefined || msg.id === null) {
                            return;
                        }
                        const key = String(msg.id);
                        if (seen.has(key)) {
                            return;
                        }
                        seen.add(key);
                        deduped.push(msg);
                    });
                    deduped.sort((a, b) => {
                        const aTimeValue = a?.timestamp ? new Date(a.timestamp).getTime() : NaN;
                        const bTimeValue = b?.timestamp ? new Date(b.timestamp).getTime() : NaN;
                        const aHasTime = Number.isFinite(aTimeValue);
                        const bHasTime = Number.isFinite(bTimeValue);
                        if (aHasTime && bHasTime && aTimeValue !== bTimeValue) {
                            return aTimeValue - bTimeValue;
                        }
                        if (aHasTime && !bHasTime) {
                            return -1;
                        }
                        if (!aHasTime && bHasTime) {
                            return 1;
                        }
                        return String(a.id).localeCompare(String(b.id));
                    });
                    return deduped;
                });
                setHasMoreMessages((prev) => prev || hasMore);
                setMessageCursor((prevCursor) => (hasMore ? cursor : prevCursor));
                refreshMessageTotal(chatId);
                setChatSearchError(null);
                setChatSearchOpen(false);
                setChatSearchResults([]);
                setChatSearchQuery("");
                window.requestAnimationFrame(() => chatMessagesRef.current?.scrollToMessage?.(messageId));
                return;
            }

            setChatSearchError(t("chatPage.search.notFound"));
        } catch (error) {
            console.error("Failed to navigate to searched message", error);
            if (!isStale()) {
                setChatSearchError(t("chatPage.search.error"));
            }
        } finally {
            if (!isStale()) {
                setChatSearchNavigating(false);
            }
        }
    }, [filterHiddenMessages, normalizeMessage, refreshMessageTotal, t]);

    const handleReactToMessage = useCallback(async (message, emoji, metadata = {}) => {
        if (!message || !message.id || !emoji) {
            return;
        }
        try {
            const response = metadata.remove
                ? await chatService.removeReaction(message.id, emoji)
                : await chatService.addReaction(message.id, emoji);
            const normalized = normalizeMessageReactions(response);
            // Trust server state to avoid local toggle races
            setMessages((prev) => prev.map((m) => (
                String(m.id) === String(message.id) ? { ...m, reactions: normalized } : m
            )));
        } catch (error) {
            console.error("Failed to update reaction:", error);
        }
    }, []);

    const handlePinToggle = useCallback((messageId) => {
        if (!messageId) {
            return;
        }
        setPinnedMessageIds((prev) => {
            const next = new Set(prev);
            const key = String(messageId);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });
    }, []);

    const handleAttachmentDownload = useCallback((messageId) => {
        if (!messageId) {
            return;
        }
        setDownloadingAttachments((prev) => {
            const next = new Set(prev);
            next.add(String(messageId));
            return next;
        });
    }, []);

    const handleAttachmentDownloadComplete = useCallback((messageId) => {
        if (!messageId) {
            return;
        }
        setDownloadingAttachments((prev) => {
            if (!prev.has(String(messageId))) {
                return prev;
            }
            const next = new Set(prev);
            next.delete(String(messageId));
            return next;
        });
    }, []);

    // 2b) Reusable function to fetch the entire chat list
    const preloadPresence = useCallback(async (userIds) => {
        if (!Array.isArray(userIds) || userIds.length === 0) {
            return;
        }
        const normalizedUserIds = Array.from(new Set(
            userIds
                .map((userId) => normalizeId(userId))
                .filter(Boolean)
        ));
        if (normalizedUserIds.length === 0) {
            return;
        }
        try {
            const statuses = await fetchPresenceStatuses(normalizedUserIds);
            if (statuses.length > 0) {
                upsertMany(statuses);
            }
        } catch (error) {
            // Swallow presence errors; UI will show offline fallback.
        }
    }, [upsertMany]);

    const resolveUserName = useCallback((userId) => {
        if (!userId) {
            return null;
        }

        const key = String(userId);

        if (userDirectoryRef.current.has(key)) {
            const cached = userDirectoryRef.current.get(key);
            if (typeof cached === "string") {
                return { name: cached, avatarUrl: null };
            }
            return cached;
        }

        const chatMatch = chatsRef.current.find(
            (chat) =>
                isDirectChatContext(chat) &&
                normalizeId(chat.avatarUserId) === key
        );

        if (chatMatch) {
            const profile = {
                name: chatMatch.username,
                avatarUrl: chatMatch.avatarUrl || null,
            };
            userDirectoryRef.current.set(key, profile);
            return profile;
        }

        const friendMatch = friendsRef.current.find((friend) => {
            const candidate = friend?.id ?? friend?.userId;
            return candidate && String(candidate) === key;
        });

        if (friendMatch) {
            const friendName = resolveUserDisplayName(friendMatch);

            const profile = {
                name: friendName,
                avatarUrl: friendMatch.avatarUrl || friendMatch.profilePicture || null,
            };

            if (profile.name || profile.avatarUrl) {
                userDirectoryRef.current.set(key, profile);
                return profile;
            }
        }

        return null;
    }, []);

    const resolveUserProfile = useCallback(async (userId) => {
        const key = normalizeId(userId);
        if (!key) {
            return { userId: null, name: t("chatPage.anonymousUser"), avatarUrl: null };
        }

        if (currentUserIdString && key === currentUserIdString) {
            const selfProfile = { userId: key, name: selfDisplayName, avatarUrl: null };
            userDirectoryRef.current.set(key, selfProfile);
            return selfProfile;
        }

        const cached = resolveUserName(key);
        if (cached?.name || cached?.avatarUrl) {
            return { userId: key, name: cached.name || t("chatPage.anonymousUser"), avatarUrl: cached.avatarUrl || null };
        }

        try {
            const userRes = await axios.get(
                `${API_URL}/api/users/${key}`,
                { withCredentials: true }
            );
            const resolvedProfile = {
                userId: key,
                name: resolveUserDisplayName(userRes.data, t("chatPage.anonymousUser")),
                avatarUrl: userRes.data.avatarUrl || userRes.data.profilePicture || null,
            };
            userDirectoryRef.current.set(key, resolvedProfile);
            return resolvedProfile;
        } catch (error) {
            console.error("Error fetching user details:", error);
            const fallbackProfile = { userId: key, name: t("chatPage.anonymousUser"), avatarUrl: null };
            userDirectoryRef.current.set(key, fallbackProfile);
            return fallbackProfile;
        }
    }, [currentUserIdString, resolveUserName, selfDisplayName, t]);

    const buildGroupChatTitle = useCallback((profiles) => {
        const names = (profiles || [])
            .map((profile) => profile?.name)
            .filter(Boolean);
        if (names.length === 0) {
            return t("chatPage.groupChat.fallbackTitle");
        }
        if (names.length <= 3) {
            return names.join(", ");
        }
        return t("chatPage.groupChat.titleWithOverflow", {
            names: names.slice(0, 3).join(", "),
            count: names.length - 3,
        });
    }, [t]);

    const buildGroupChatSummary = useCallback(async (chat, {
        latestMessage = NO_CHAT_PREVIEW,
        lastMessageTimestamp = null,
        unreadMessages = 0,
    } = {}) => {
        const participantIds = normalizeParticipantIds(chat?.participantIds);
        const memberIds = participantIds.filter((participantId) => participantId !== currentUserIdString);
        const memberProfiles = await Promise.all(memberIds.map((participantId) => resolveUserProfile(participantId)));
        const explicitTitle = typeof chat?.title === "string" ? chat.title.trim() : "";
        const title = explicitTitle || buildGroupChatTitle(memberProfiles);

        return {
            id: chat.id,
            contextType: CHAT_CONTEXT_TYPES.GROUP,
            username: title,
            latestMessage,
            lastMessageTimestamp,
            unreadMessages,
            participantIds,
            participantCount: participantIds.length,
            groupMemberProfiles: memberProfiles,
            avatarUserId: null,
            avatarUrl: null,
            isGroup: true,
            presenceStatus: null,
            online: false,
            lastSeen: null,
        };
    }, [buildGroupChatTitle, currentUserIdString, resolveUserProfile]);

    const sendTypingStatus = useCallback((chatId, typing) => {
        if (!chatId) {
            return;
        }

        axios
            .post(
                `${CHAT_SERVICE_URL}/api/chat/${chatId}/typing`,
                { typing },
                { withCredentials: true }
            )
            .catch(() => {
                // Silently ignore typing network errors; indicator is best-effort.
            });
    }, []);

    const updateOutgoingTyping = useCallback((chatId, typing) => {
        if (!chatId) {
            return;
        }

        const key = String(chatId);
        const entry = typingPublishRef.current.get(key) || { state: false, timeoutId: null };

        if (typing) {
            if (!entry.state) {
                sendTypingStatus(key, true);
                entry.state = true;
            }
            if (entry.timeoutId) {
                clearTimeout(entry.timeoutId);
            }
            entry.timeoutId = window.setTimeout(() => {
                entry.state = false;
                sendTypingStatus(key, false);
                typingPublishRef.current.set(key, entry);
            }, 2500);
        } else {
            if (entry.state) {
                sendTypingStatus(key, false);
            }
            if (entry.timeoutId) {
                clearTimeout(entry.timeoutId);
                entry.timeoutId = null;
            }
            entry.state = false;
        }

        typingPublishRef.current.set(key, entry);
    }, [sendTypingStatus]);

    const togglePinnedChat = useCallback((chatId) => {
        if (!chatId) {
            return;
        }
        const key = String(chatId);
        setPinnedChatIds((prev) => {
            const next = new Set(prev);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });
    }, []);

    const toggleMutedChat = useCallback((chatId) => {
        if (!chatId) {
            return;
        }
        const key = String(chatId);
        setMutedChatIds((prev) => {
            const next = new Set(prev);
            if (next.has(key)) {
                next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });
    }, []);

    const handleFilterChange = useCallback((nextFilter) => {
        setChatFilter((prev) => (prev === nextFilter ? prev : nextFilter));
    }, []);

    const handleThreadSearchChange = useCallback((event) => {
        setThreadQuery(event.target.value);
    }, []);

    const handleThreadSearchClear = useCallback(() => {
        setThreadQuery("");
    }, []);

    const handleTypingSignal = useCallback((typing) => {
        if (!selectedChatId) {
            return;
        }
        updateOutgoingTyping(selectedChatId, typing);
    }, [selectedChatId, updateOutgoingTyping]);

    const composerMentionUsers = useMemo(() => (
        (Array.isArray(friends) ? friends : [])
            .map((friend) => {
                const userId = friend?.id ?? friend?.userId;
                if (userId === undefined || userId === null) {
                    return null;
                }
                const resolvedName = resolveUserUsername(friend)
                    || resolveUserDisplayName(friend)
                    || String(userId);
                return {
                    userId: String(userId),
                    username: resolvedName,
                };
            })
            .filter(Boolean)
    ), [friends]);

    const handleComposerDraftChange = useCallback((nextHtml) => {
        const resolvedHtml = typeof nextHtml === "string" ? nextHtml : "";
        setChatDraftHtml(resolvedHtml);
        if (composerError) {
            setComposerError(null);
        }
        handleTypingSignal(Boolean(toPlainText(resolvedHtml)));
    }, [composerError, handleTypingSignal]);

    const handleSendComposedMessage = useCallback(async (next) => {
        const draftHtml = (next && typeof next === "object" && typeof next.html === "string")
            ? next.html
            : (typeof next === "string" ? next : (typeof chatDraftHtml === "string" ? chatDraftHtml : ""));
        const chatId = selectedChatId;
        let html = sanitizeHtml(draftHtml);
        if (!hasVisibleContent(html) || !chatId || isComposerSending) {
            return false;
        }

        setComposerSendingForChat(chatId, true);
        if (String(selectedChatIdRef.current ?? "") === String(chatId)) {
            setComposerError(null);
        }

        const isActiveChatRequest = () => String(selectedChatIdRef.current ?? "") === String(chatId);

        try {
            let attachments = [];
            if (next && typeof next === "object" && Array.isArray(next.pendingFiles) && next.pendingFiles.length) {
                const blobUrlsInDraft = getBlobImageSrcSet(html);
                const pendingFilesInDraft = next.pendingFiles.filter((entry) => (
                    Array.isArray(entry)
                    && entry.length >= 2
                    && entry[0]
                    && entry[1]
                    && blobUrlsInDraft.has(String(entry[0]))
                ));
                const replacements = [];
                let failedUploads = 0;

                for (const [blobUrl, file] of pendingFilesInDraft) {
                    try {
                        if (!blobUrl || !file) {
                            continue;
                        }
                        const meta = await uploadChatAttachment(String(chatId), file);
                        if (meta && meta.url) {
                            replacements.push({ blobUrl: String(blobUrl), serverUrl: String(meta.url) });
                            attachments.push({
                                id: meta.id,
                                type: meta.type,
                                objectKey: meta.objectKey,
                                url: meta.url,
                                contentType: meta.contentType,
                                sizeBytes: meta.sizeBytes,
                                width: meta.width,
                                height: meta.height,
                                checksum: meta.checksum,
                                thumbnailUrl: meta.thumbnailUrl,
                                createdAt: meta.createdAt,
                            });
                        }
                    } catch (error) {
                        failedUploads += 1;
                        // eslint-disable-next-line no-console
                        console.error("Failed to upload chat attachment", error);
                    }
                }

                if (failedUploads > 0) {
                    if (isActiveChatRequest()) {
                        setComposerError(t("chatPage.composer.uploadFailed"));
                    }
                    return false;
                }

                if (replacements.length) {
                    let replacedHtml = html;
                    replacements.forEach(({ blobUrl, serverUrl }) => {
                        try {
                            replacedHtml = replacedHtml.split(blobUrl).join(serverUrl);
                        } catch (_) {
                            // ignore malformed replacements
                        }
                    });
                    html = sanitizeHtml(replacedHtml);
                }

                html = stripBlobImgTags(html);
            }

            html = stripBlobImgTags(html);
            if (!hasVisibleContent(html) && attachments.length === 0) {
                if (isActiveChatRequest()) {
                    setComposerError(t("chatPage.composer.emptyMessage"));
                }
                return false;
            }

            await chatService.sendMessage({
                chatId,
                contentHtml: html,
                attachments,
                replyToMessageId: replyingTo?.id,
            });
            if (isActiveChatRequest()) {
                setChatDraftHtml("");
                setReplyingTo(null);
                setEditingMessage(null);
                setComposerError(null);
            }
            updateOutgoingTyping(chatId, false);
            return true;
        } catch (error) {
            console.error("❌ Error sending message:", error);
            if (isActiveChatRequest()) {
                setComposerError(t("chatPage.composer.sendFailed"));
            }
            return false;
        } finally {
            setComposerSendingForChat(chatId, false);
        }
    }, [chatDraftHtml, isComposerSending, replyingTo?.id, selectedChatId, setComposerSendingForChat, t, updateOutgoingTyping]);

    useEffect(() => {
        if (previousChatIdRef.current && previousChatIdRef.current !== selectedChatId) {
            updateOutgoingTyping(previousChatIdRef.current, false);
        }
        previousChatIdRef.current = selectedChatId;
    }, [selectedChatId, updateOutgoingTyping]);

    const updateTypingIndicators = useCallback(
        (chatId, userId, isTyping) => {
            if (!chatId || !userId) {
                return;
            }

            if (currentUserId && String(userId) === String(currentUserId)) {
                return;
            }

            const key = String(chatId);
            const userKey = String(userId);
            const timeoutKey = `${key}:${userKey}`;

            setTypingIndicators((prev) => {
                const existing = prev[key] ? { ...prev[key] } : {};
                if (isTyping) {
                    existing[userKey] = true;
                } else {
                    delete existing[userKey];
                }
                return { ...prev, [key]: existing };
            });

            if (isTyping) {
                if (typingTimeoutsRef.current.has(timeoutKey)) {
                    clearTimeout(typingTimeoutsRef.current.get(timeoutKey));
                }
                const timeoutId = window.setTimeout(() => {
                    typingTimeoutsRef.current.delete(timeoutKey);
                    setTypingIndicators((prev) => {
                        const existing = prev[key] ? { ...prev[key] } : {};
                        if (!existing[userKey]) {
                            return prev;
                        }
                        delete existing[userKey];
                        return { ...prev, [key]: existing };
                    });
                }, 4000);
                typingTimeoutsRef.current.set(timeoutKey, timeoutId);
            } else {
                const timeoutId = typingTimeoutsRef.current.get(timeoutKey);
                if (timeoutId) {
                    clearTimeout(timeoutId);
                    typingTimeoutsRef.current.delete(timeoutKey);
                }
            }
        },
        [currentUserId]
    );

    const fetchChatsList = useCallback(async () => {
        if (!currentUserIdString) {
            return;
        }
        setChatsLoading(true);
        try {
            const response = await axios.get(`${CHAT_SERVICE_URL}/api/chat/list`, {
                withCredentials: true,
            });

            if (!Array.isArray(response.data)) {
                console.error("❌ Error: Expected an array but got:", response.data);
                setChatsLoading(false);
                return;
            }

            // Only show direct chats and ad hoc group chats in the Chat page.
            // Workspace group channels are rendered in the Groups workspace.
            const conversationChats = response.data.filter(
                (chat) =>
                    chat &&
                    (isDirectChatContext(chat) || isGroupChatContext(chat)) &&
                    Array.isArray(chat.participantIds)
            );

            const presenceCandidates = new Set();

            const updatedChats = await Promise.all(
                conversationChats.map(async (chat) => {
                    let latestMessage = NO_CHAT_PREVIEW;
                    let lastMessageTimestamp = null;
                    let unreadMessages = 0;

                    unreadMessages = await fetchChatUnreadCount(chat.id);

                    // Fetch last message preview + timestamp
                    try {
                        const preview = await fetchLatestVisiblePreview(chat.id);
                        latestMessage = preview.latestMessage;
                        lastMessageTimestamp = preview.lastMessageTimestamp;
                    } catch (err) {
                        console.error(`Error fetching messages for chat ${chat.id}:`, err);
                    }

                    if (isGroupChatContext(chat)) {
                        return buildGroupChatSummary(chat, {
                            latestMessage,
                            lastMessageTimestamp,
                            unreadMessages,
                        });
                    }

                    const identity = resolveDirectChatIdentity({
                        participantIds: chat.participantIds,
                        currentUserId: currentUserIdString,
                    });
                    const peerUserId = identity.userId;
                    const isPersonal = identity.isPersonal;
                    const normalizedParticipantIds = identity.participantIds;

                    if (!peerUserId) {
                        // Single-participant or data mismatch
                        return {
                            id: chat.id,
                            contextType: CHAT_CONTEXT_TYPES.DIRECT,
                            username: "Unknown User",
                            latestMessage,
                            lastMessageTimestamp,
                            unreadMessages,
                            participantIds: normalizedParticipantIds,
                            avatarUserId: null,
                            isPersonal: false,
                            presenceStatus: "offline",
                            online: false,
                            lastSeen: null,
                        };
                    }

                    if (isPersonal) {
                        const resolvedProfile = {
                            name: selfDisplayName,
                            avatarUrl: null,
                        };
                        userDirectoryRef.current.set(peerUserId, resolvedProfile);
                        presenceCandidates.add(String(peerUserId));
                        return {
                            id: chat.id,
                            contextType: CHAT_CONTEXT_TYPES.DIRECT,
                            username: selfDisplayName,
                            latestMessage,
                            lastMessageTimestamp,
                            unreadMessages,
                            participantIds: normalizedParticipantIds,
                            avatarUserId: peerUserId,
                            avatarUrl: null,
                            isPersonal: true,
                            presenceStatus: "offline",
                            online: false,
                            lastSeen: null,
                        };
                    }

                    // Fetch "other" user's username
                    try {
                        const userRes = await axios.get(
                            `${API_URL}/api/users/${peerUserId}`,
                            { withCredentials: true }
                        );
                        const key = String(peerUserId);
                        presenceCandidates.add(key);
                        const resolvedName = resolveUserDisplayName(userRes.data, "Unknown User");
                        const resolvedProfile = {
                            name: resolvedName,
                            avatarUrl: userRes.data.avatarUrl || userRes.data.profilePicture || null,
                        };
                        userDirectoryRef.current.set(key, resolvedProfile);
                        return {
                            id: chat.id,
                            contextType: CHAT_CONTEXT_TYPES.DIRECT,
                            username: resolvedName,
                            latestMessage,
                            lastMessageTimestamp,
                            unreadMessages,
                            participantIds: normalizedParticipantIds,
                            avatarUserId: peerUserId,
                            avatarUrl: userRes.data.avatarUrl || userRes.data.profilePicture || null,
                            isPersonal: false,
                        };
                    } catch (err) {
                        console.error("Error fetching user details:", err);
                        if (peerUserId !== undefined && peerUserId !== null) {
                            const key = String(peerUserId);
                            presenceCandidates.add(key);
                            if (!userDirectoryRef.current.has(key)) {
                                userDirectoryRef.current.set(key, { name: "Unknown User", avatarUrl: null });
                            }
                        }
                        return {
                            id: chat.id,
                            contextType: CHAT_CONTEXT_TYPES.DIRECT,
                            username: "Unknown User",
                            latestMessage,
                            lastMessageTimestamp,
                            unreadMessages,
                            participantIds: normalizedParticipantIds,
                            avatarUserId: peerUserId,
                            avatarUrl: null,
                            isPersonal: false,
                        };
                    }
                })
            );

            // Sort by timestamp desc
            updatedChats.sort(
                (a, b) => toMillis(b.lastMessageTimestamp) - toMillis(a.lastMessageTimestamp)
            );
            setChats(updatedChats);
            setChatsLoading(false);

            if (presenceCandidates.size > 0) {
                preloadPresence(Array.from(presenceCandidates));
            }
        } catch (error) {
            console.error("Error fetching chats:", error);
            setChatsLoading(false);
        }
    }, [buildGroupChatSummary, currentUserIdString, preloadPresence, fetchChatUnreadCount, fetchLatestVisiblePreview, selfDisplayName]);

    useEffect(() => {
        if (typeof window === "undefined") {
            return;
        }

        const applyChatSelectionIntent = (chatId, { refreshIfMissing = false } = {}) => {
            const normalizedChatId = normalizeSelectedChatId(chatId);
            if (!normalizedChatId) {
                return;
            }
            initialSelectedChatIdRef.current = normalizedChatId;
            initialSelectionSettledRef.current = false;

            const hasMatch = chatsRef.current.some((chat) => String(chat.id) === normalizedChatId);
            if (hasMatch) {
                selectChat(normalizedChatId);
                initialSelectedChatIdRef.current = null;
                return;
            }

            if (refreshIfMissing) {
                fetchChatsList();
            }
        };

        const handleNavigateTabIntent = (event) => {
            if (event?.detail?.tabId !== CHAT_TAB_ID) {
                return;
            }
            const requestedChatId = normalizeSelectedChatId(event?.detail?.chatId);
            if (!requestedChatId) {
                return;
            }
            applyChatSelectionIntent(requestedChatId, { refreshIfMissing: true });
        };

        window.addEventListener(NAVIGATE_TAB_EVENT, handleNavigateTabIntent);
        return () => {
            window.removeEventListener(NAVIGATE_TAB_EVENT, handleNavigateTabIntent);
        };
    }, [fetchChatsList, selectChat]);

    const unreadCount = useMemo(
        () => chats.reduce((sum, chat) => sum + (chat.unreadMessages || 0), 0),
        [chats]
    );

    useEffect(() => {
        setTotalUnread(unreadCount);
    }, [unreadCount, setTotalUnread]);

    // 3) subscribeToChat: listens for message events on a specific chat
    const sortedChats = useMemo(() => {
        const comparator = (a, b) => toMillis(b.lastMessageTimestamp) - toMillis(a.lastMessageTimestamp);
        const pinned = [];
        const regular = [];

        chats.forEach((chat) => {
            const key = String(chat.id);
            if (pinnedChatIds.has(key)) {
                pinned.push(chat);
            } else {
                regular.push(chat);
            }
        });

        pinned.sort(comparator);
        regular.sort(comparator);

        return [...pinned, ...regular];
    }, [chats, pinnedChatIds]);

    const visibleChats = useMemo(() => {
        const normalizedQuery = threadQuery.trim().toLowerCase();
        return sortedChats.filter((chat) => {
            const key = String(chat.id);
            const matchesQuery =
                normalizedQuery.length === 0 ||
                chat.username?.toLowerCase().includes(normalizedQuery) ||
                chat.latestMessage?.toLowerCase().includes(normalizedQuery);

            if (!matchesQuery) {
                return false;
            }

            switch (chatFilter) {
                case "unread":
                    return (chat.unreadMessages || 0) > 0;
                case "pinned":
                    return pinnedChatIds.has(key);
                case "muted":
                    return mutedChatIds.has(key);
                default:
                    return true;
            }
        });
    }, [sortedChats, threadQuery, chatFilter, pinnedChatIds, mutedChatIds]);

    /* Labelled here rather than at each write site: storing the localized text
       would freeze whichever language was active when the chat was first seen,
       and the next real message has to be able to replace it outright. */
    const localizedVisibleChats = useMemo(
        () => applyEmptyPreviewLabel(visibleChats, t("chatPage.threads.emptyPreview")),
        [t, visibleChats],
    );

    const activeChat = useMemo(
        () => chats.find((chat) => String(chat.id) === String(selectedChatId)) || null,
        [chats, selectedChatId]
    );
    const activeChatIsGroup = useMemo(() => isGroupChatContext(activeChat), [activeChat]);

    const activeChatParticipantIds = useMemo(() => {
        if (!activeChat || !Array.isArray(activeChat.participantIds)) {
            return [];
        }
        return activeChat.participantIds
            .filter((id) => id !== undefined && id !== null)
            .map((id) => String(id));
    }, [activeChat]);

    const resolveMessageStatus = useCallback((message) => {
        if (!message || !currentUserIdString) {
            return null;
        }
        const senderId = message.senderId !== undefined && message.senderId !== null
            ? String(message.senderId)
            : null;
        if (senderId !== currentUserIdString) {
            return null;
        }

        const status = typeof message.deliveryStatus === "string"
            ? message.deliveryStatus.toUpperCase()
            : null;

        if (status === "SEEN" || status === "DELIVERED") {
            return status;
        }

        if (Array.isArray(message.seenBy) && activeChatParticipantIds.length > 0) {
            const recipients = activeChatParticipantIds.filter((id) => id !== currentUserIdString);
            if (recipients.length > 0) {
                const seenBySet = new Set(message.seenBy.map(String));
                const allSeen = recipients.every((id) => seenBySet.has(id));
                if (allSeen) {
                    return "SEEN";
                }
            }
        }

        return status;
    }, [activeChatParticipantIds, currentUserIdString]);

    const resolveChatPresenceSnapshot = useCallback((chat) => {
        if (!chat) {
            return {
                status: "offline",
                online: false,
                lastSeen: null,
            };
        }

        const candidateUserId = normalizeId(chat.avatarUserId);
        const liveSnapshot = candidateUserId ? getStatus(candidateUserId) : null;
        if (liveSnapshot) {
            const liveStatus = typeof liveSnapshot.status === "string"
                ? liveSnapshot.status.toLowerCase()
                : null;
            const hasExplicitStatus = liveStatus === "online" || liveStatus === "offline" || liveStatus === "away";
            const liveOnline = typeof liveSnapshot.online === "boolean"
                ? liveSnapshot.online
                : liveStatus === "online";
            return {
                status: hasExplicitStatus ? liveStatus : (liveOnline ? "online" : "offline"),
                online: liveOnline,
                lastSeen: liveOnline ? null : (liveSnapshot.lastSeen ?? null),
            };
        }

        const fallbackStatus = typeof chat.presenceStatus === "string"
            ? chat.presenceStatus.toLowerCase()
            : null;
        if (fallbackStatus === "online" || fallbackStatus === "offline" || fallbackStatus === "away") {
            const fallbackOnline = fallbackStatus === "online";
            return {
                status: fallbackStatus,
                online: fallbackOnline,
                lastSeen: fallbackOnline ? null : (chat.lastSeen ?? null),
            };
        }

        if (typeof chat.online === "boolean") {
            return {
                status: chat.online ? "online" : "offline",
                online: chat.online,
                lastSeen: chat.online ? null : (chat.lastSeen ?? null),
            };
        }

        return {
            status: "offline",
            online: false,
            lastSeen: chat.lastSeen ?? null,
        };
    }, [getStatus]);

    const pinnedCount = pinnedChatIds.size;
    const mutedCount = mutedChatIds.size;

    const onlineCount = useMemo(() => {
        if (!Array.isArray(chats) || chats.length === 0) {
            return 0;
        }
        let online = 0;
        chats.forEach((chat) => {
            const snapshot = resolveChatPresenceSnapshot(chat);
            if (snapshot.online) {
                online += 1;
            }
        });
        return online;
    }, [chats, resolveChatPresenceSnapshot]);

    const threadFilters = useMemo(
        () => [
            { id: "all", label: t("chatPage.filters.all"), badge: sortedChats.length },
            { id: "unread", label: t("chatPage.filters.unread"), badge: unreadCount, Icon: ChatFilterUnreadIcon },
            { id: "pinned", label: t("chatPage.filters.pinned"), badge: pinnedCount, Icon: ChatFilterPinnedIcon },
            { id: "muted", label: t("chatPage.filters.muted"), badge: mutedCount, Icon: ChatFilterMutedIcon },
        ],
        [sortedChats.length, unreadCount, pinnedCount, mutedCount, t]
    );

    const threadEmptyMessage = useMemo(() => {
        if (sortedChats.length === 0) {
            return t("chatPage.threads.empty.noChats");
        }
        switch (chatFilter) {
            case "unread":
                return t("chatPage.threads.empty.unread");
            case "pinned":
                return t("chatPage.threads.empty.pinned");
            case "muted":
                return t("chatPage.threads.empty.muted");
            default:
                return t("chatPage.threads.empty.filtered");
        }
    }, [chatFilter, sortedChats.length, t]);

    const formatRelativeTime = useCallback((timestamp) => {
        const millis = toMillis(timestamp);
        if (!millis) {
            return t("chatPage.relativeTime.recently");
        }

        const now = Date.now();
        const diffMs = now - millis;

        if (diffMs <= 0) {
            return t("chatPage.relativeTime.justNow");
        }

        const diffMinutes = Math.round(diffMs / 60000);
        if (diffMinutes < 1) {
            return t("chatPage.relativeTime.justNow");
        }
        if (diffMinutes < 60) {
            return t("chatPage.relativeTime.minutesAgo", { count: diffMinutes });
        }

        const diffHours = Math.round(diffMinutes / 60);
        if (diffHours < 24) {
            return t("chatPage.relativeTime.hoursAgo", { count: diffHours });
        }

        const diffDays = Math.round(diffHours / 24);
        if (diffDays < 7) {
            return t("chatPage.relativeTime.daysAgo", { count: diffDays });
        }

        const date = new Date(millis);
        // Month names have to follow the chosen language, not the browser's.
        return date.toLocaleDateString(i18n.language, { month: "short", day: "numeric" });
    }, [i18n.language, t]);

    const activeChatPresenceState = useMemo(
        () => (activeChatIsGroup ? null : resolveChatPresenceSnapshot(activeChat)),
        [activeChat, activeChatIsGroup, resolveChatPresenceSnapshot]
    );

    const activeChatPresenceRecord = useMemo(() => {
        if (!activeChat || activeChatIsGroup || !activeChatPresenceState) {
            return null;
        }
        return {
            status: activeChatPresenceState.status,
            lastSeen: activeChatPresenceState.lastSeen,
        };
    }, [activeChat, activeChatIsGroup, activeChatPresenceState]);

    const activeChatAvatarStatus = useMemo(() => {
        if (!activeChat || activeChatIsGroup || !activeChatPresenceState) {
            return undefined;
        }
        return {
            online: activeChatPresenceState.online,
            lastSeen: activeChatPresenceState.online ? null : activeChatPresenceState.lastSeen,
            placement: "br",
            size: "14px",
            offset: "2px",
        };
    }, [activeChat, activeChatIsGroup, activeChatPresenceState]);

    const activeChatAvatarProps = useMemo(
        () => resolveChatAvatarProps(activeChat),
        [activeChat]
    );

    const messageListLength = messages.length;

    const messageCount = useMemo(() => {
        if (!activeChat) {
            return 0;
        }
        const key = String(activeChat.id);
        const stored = messageTotals[key];
        if (Number.isFinite(stored)) {
            return stored;
        }
        return messageListLength;
    }, [activeChat, messageTotals, messageListLength]);

    const conversationTags = useMemo(() => {
        if (!activeChat) {
            return [];
        }
        const key = String(activeChat.id);
        const tags = [];
        // Pinned state is shown as a compact icon badge next to the name
        // (see `conversationTitleBadge`), not as a labelled tag here.
        if (mutedChatIds.has(key)) {
            tags.push({
                id: "muted",
                label: "Muted",
                icon: (
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                        <path
                            fill="currentColor"
                            d="M12 3a1 1 0 0 1 1 1v4.586l4.707-4.707a1 1 0 0 1 1.414 1.414L14.414 10H17a1 1 0 0 1 .832 1.555l-10 14a1 1 0 0 1-1.832-.555V17H5a3 3 0 0 1-3-3V8a1 1 0 0 1 2 0v6a1 1 0 0 0 1 1h.586l5.414-7.58V4a1 1 0 0 1 1-1z"
                            opacity="0.55"
                        />
                        <path
                            fill="currentColor"
                            d="M4.21 3.79a1 1 0 1 1 1.58-1.18l14 18a1 1 0 0 1-1.58 1.18z"
                        />
                    </svg>
                ),
                variant: "muted",
            });
        }
        return tags;
    }, [activeChat, mutedChatIds]);

    const conversationTitleBadge = useMemo(() => {
        if (!activeChat || !pinnedChatIds.has(String(activeChat.id))) {
            return null;
        }
        return (
            <span
                className="chat-conversation__title-badge"
                role="img"
                aria-label={t("chatPage.pinned.badgeLabel")}
                title={t("chatPage.pinned.badgeLabel")}
            >
                <ChatFilterPinnedIcon aria-hidden="true" focusable="false" />
            </span>
        );
    }, [activeChat, pinnedChatIds, t]);

    const conversationSecondaryItems = useMemo(() => {
        if (!activeChat) {
            return [];
        }
        const items = [];
        if (isGroupChatContext(activeChat)) {
            items.push({
                id: "participants",
                label: t("chatPage.groupChat.participants", {
                    count: activeChat.participantCount || activeChatParticipantIds.length,
                }),
                icon: (
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                        <path
                            fill="currentColor"
                            d="M8 11a4 4 0 1 1 4-4 4 4 0 0 1-4 4zm8.5 0a3.5 3.5 0 1 1 3.5-3.5 3.5 3.5 0 0 1-3.5 3.5zM3 20a5 5 0 0 1 10 0 1 1 0 0 1-1 1H4a1 1 0 0 1-1-1zm11.3 1a2.5 2.5 0 0 0 .2-1 6.9 6.9 0 0 0-1.4-4.2A4.5 4.5 0 0 1 21 18.8V20a1 1 0 0 1-1 1z"
                        />
                    </svg>
                ),
            });
        }
        items.push({
            id: "messages",
            label: t("chatPage.messageCount", { count: messageCount }),
            icon: <MessageIcon aria-hidden="true" focusable="false" />,
        });
        return items;
    }, [activeChat, activeChatParticipantIds.length, messageCount, t]);

    const conversationActions = useMemo(() => {
        if (!activeChat) {
            return [];
        }
        const isPinned = pinnedChatIds.has(String(activeChat.id));
        return [
            {
                id: "search",
                label: t("chatPage.search.action"),
                icon: (
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                        <path
                            fill="currentColor"
                            d="M10 4a6 6 0 0 1 4.472 9.984l3.772 3.772a1 1 0 0 1-1.414 1.414l-3.772-3.772A6 6 0 1 1 10 4zm0 2a4 4 0 1 0 4 4 4 4 0 0 0-4-4z"
                        />
                    </svg>
                ),
                variant: "ghost",
                onClick: handleToggleChatSearch,
                className: classNames({ "chat-conversation__action--active": chatSearchOpen }),
            },
            {
                id: "pinned-summary",
                label: t("chatPage.pinned.action"),
                render: (
                    <InfoIcon
                        key="pinned-summary"
                        text={`${t("chatPage.pinned.title")} • ${t("chatPage.pinned.subtitle")}`}
                        className="chat-conversation__action-tooltip"
                    >
                        <IconButton
                            variant="ghost"
                            label={t("chatPage.pinned.action")}
                            icon={(
                                <svg viewBox="0 0 24 24" aria-hidden="true">
                                    <path
                                        fill="currentColor"
                                        d="M12 3a9 9 0 1 1-9 9 9 9 0 0 1 9-9zm0 2a7 7 0 1 0 7 7 7 7 0 0 0-7-7z"
                                        opacity="0.35"
                                    />
                                    <path
                                        fill="currentColor"
                                        d="M11 7h2v5h-2zm0 6h2v2h-2z"
                                    />
                                </svg>
                            )}
                            onClick={() => setShowPinnedHint((prev) => !prev)}
                        />
                    </InfoIcon>
                ),
            },
            {
                id: "pin",
                label: isPinned ? t("chatPage.actions.unpin") : t("chatPage.actions.pin"),
                icon: (
                    <ChatFilterPinnedIcon className="chat-conversation__pin-icon" aria-hidden="true" focusable="false" />
                ),
                variant: "ghost",
                onClick: () => togglePinnedChat(activeChat.id),
                className: classNames({ "chat-conversation__action--active": isPinned }),
            },
        ];
    }, [activeChat, chatSearchOpen, handleToggleChatSearch, pinnedChatIds, t, togglePinnedChat]);

    const activeTypingIds = useMemo(
        () => Object.keys(typingIndicators[String(selectedChatId)] || {}),
        [typingIndicators, selectedChatId]
    );
    const typingParticipants = useMemo(() => activeTypingIds, [activeTypingIds]);

    const subscribeToChat = useCallback(
        (client, chatId) => {
            if (subscriptionsRef.current[chatId]) return; // skip

            const sub = client.subscribe(`/topic/${chatId}`, async (message) => {
                if (!message?.body) {
                    return;
                }
                let newMessage;
                try {
                    newMessage = JSON.parse(message.body);
                } catch (error) {
                    console.warn("Failed to parse chat message payload", error);
                    return;
                }
                const chatIdOfMsg = newMessage.chatId ?? chatId;

                const eventType = newMessage.type;

                switch (eventType) {
                    case "DELETE": {
                        const isActiveChat = String(chatIdOfMsg) === String(selectedChatIdRef.current);
                        const messageIdentifier = newMessage.id ?? newMessage.messageId;
                        const messageKey =
                            messageIdentifier !== undefined && messageIdentifier !== null
                                ? String(messageIdentifier)
                                : null;
                        if (messageKey) {
                            untrackHiddenMessage(messageKey, chatIdOfMsg);
                        }
                        const wasLocalDelete = messageKey
                            ? pendingDeletedMessageIdsRef.current.delete(messageKey)
                            : false;
                        setMessages((prev) => {
                            if (!messageKey) {
                                return prev.filter((m) => m.id !== newMessage.id);
                            }
                            return prev.filter((m) => String(m.id) !== messageKey);
                        });
                        if (!wasLocalDelete && isActiveChat && messageKey) {
                            adjustMessageTotal(chatIdOfMsg, -1);
                        }

                        try {
                            const unreadResponse = await axios.get(
                                `${CHAT_SERVICE_URL}/api/chat/${chatIdOfMsg}/unread-count/${currentUserId}`,
                                { withCredentials: true }
                            );
                            const updatedUnreadCount = unreadResponse.data;
                            setChats((prevChats) =>
                                prevChats.map((c) =>
                                    c.id === chatIdOfMsg
                                        ? {
                                            ...c,
                                            unreadMessages:
                                                chatIdOfMsg === selectedChatIdRef.current
                                                    ? 0
                                                    : updatedUnreadCount,
                                        }
                                        : c
                                )
                            );
                        } catch (err) {
                            console.error(
                                `Error fetching unread count for chat ${chatIdOfMsg}:`,
                                err
                            );
                        }

                        fetchLatestVisiblePreview(chatIdOfMsg).then((preview) => {
                            const nextContent = preview.latestMessage;
                            const nextTimestamp = preview.lastMessageTimestamp;
                            setChats((prevChats) =>
                                prevChats.map((c) =>
                                    c.id === chatIdOfMsg
                                        ? {
                                            ...c,
                                            latestMessage: nextContent,
                                            lastMessageTimestamp: nextTimestamp ?? c.lastMessageTimestamp,
                                        }
                                        : c
                                )
                            );
                        });
                        break;
                    }

                    case "COMMENT_ADD":
                    case "COMMENT_EDIT":
                    case "COMMENT_DELETE": {
                        // Comment events are handled by the MessageCommentDrawer.
                        // They must not mutate the main message list to avoid
                        // clobbering immutable fields like timestamp.
                        break;
                    }

                    case "EDIT": {
                        setMessages((prev) =>
                            prev.map((m) =>
                                m.id === newMessage.id
                                    ? normalizeMessage({
                                        ...m,
                                        ...newMessage,
                                        senderId: m.senderId,
                                        seenBy: m.seenBy,
                                        deliveryStatus: m.deliveryStatus,
                                    })
                                    : m
                            )
                        );
                        fetchLatestVisiblePreview(chatIdOfMsg).then((preview) => {
                            const nextContent = preview.latestMessage;
                            const nextTimestamp = preview.lastMessageTimestamp;
                            setChats((prevChats) =>
                                prevChats.map((c) =>
                                    c.id === chatIdOfMsg
                                        ? {
                                            ...c,
                                            latestMessage: nextContent,
                                            lastMessageTimestamp: nextTimestamp ?? c.lastMessageTimestamp,
                                        }
                                        : c
                                )
                            );
                        });
                        break;
                    }

                    case "MESSAGE_STATUS": {
                        const readerId = newMessage.userId ? String(newMessage.userId) : null;
                        const lastReadRaw = newMessage.lastReadTimestamp;
                        if (!readerId || !lastReadRaw) {
                            break;
                        }

                        const lastReadMs = new Date(lastReadRaw).getTime();
                        if (Number.isNaN(lastReadMs)) {
                            break;
                        }

                        const participants = chatsRef.current.find(
                            (chat) => String(chat.id) === String(chatIdOfMsg)
                        )?.participantIds || [];

                        setMessages((prev) =>
                            prev.map((m) => {
                                if (String(m.chatId) !== String(chatIdOfMsg)) {
                                    return m;
                                }
                                if (String(m.senderId) === readerId) {
                                    return m;
                                }

                                const messageTime = new Date(m.timestamp).getTime();
                                if (Number.isNaN(messageTime) || messageTime > lastReadMs) {
                                    return m;
                                }

                                const seenBySet = new Set(
                                    Array.isArray(m.seenBy) ? m.seenBy.map(String) : []
                                );
                                seenBySet.add(readerId);

                                const recipients = participants.filter(
                                    (participantId) => String(participantId) !== String(m.senderId)
                                );
                                const shouldBeSeen =
                                    recipients.length === 0 || seenBySet.size >= recipients.length;

                                return {
                                    ...m,
                                    seenBy: Array.from(seenBySet),
                                    deliveryStatus: shouldBeSeen ? "SEEN" : "DELIVERED",
                                };
                            })
                        );
                        break;
                    }

                    case "TYPING": {
                        updateTypingIndicators(chatIdOfMsg, newMessage.userId, Boolean(newMessage.typing));
                        break;
                    }

                    case "REACTIONS": {
                        if (newMessage.messageId) {
                            setMessages((prev) => prev.map((msg) => {
                                if (String(msg.id) !== String(newMessage.messageId)) {
                                    return msg;
                                }
                                const merged = mergeMessageReactions(
                                    msg.reactions,
                                    newMessage.reactions,
                                    {
                                        currentUserId,
                                        actorId: newMessage.actorId,
                                        emoji: newMessage.emoji,
                                        action: newMessage.action,
                                    }
                                );
                                return { ...msg, reactions: merged };
                            }));
                        }
                        break;
                    }

                    default: {
                        const normalizedMessage = normalizeMessage(newMessage);
                        const isActiveChat = String(chatIdOfMsg) === String(selectedChatIdRef.current);

                        const hiddenSet = hiddenMessagesRef.current.get(String(chatIdOfMsg));
                        if (hiddenSet && normalizedMessage?.id && hiddenSet.has(String(normalizedMessage.id))) {
                            break;
                        }

                        // Possibly a new message in this chat
                        try {
                            const unreadResponse = await axios.get(
                                `${CHAT_SERVICE_URL}/api/chat/${chatIdOfMsg}/unread-count/${currentUserId}`,
                                { withCredentials: true }
                            );
                            const updatedUnreadCount = unreadResponse.data;

                            setChats((prevChats) => {
                                const updated = prevChats.map((c) =>
                                    c.id === chatIdOfMsg
                                        ? {
                                            ...c,
                                            latestMessage: normalizedMessage.content,
                                            lastMessageTimestamp:
                                                normalizedMessage.timestamp || c.lastMessageTimestamp,
                                            unreadMessages:
                                                chatIdOfMsg === selectedChatIdRef.current
                                                    ? 0
                                                    : updatedUnreadCount,
                                        }
                                        : c
                                );
                                return updated.sort(
                                    (a, b) =>
                                        toMillis(b.lastMessageTimestamp) - toMillis(a.lastMessageTimestamp)
                                );
                            });
                        } catch (err) {
                            console.error(
                                `Error fetching unread count for chat ${chatIdOfMsg}:`,
                                err
                            );
                        }

                        // If the message is for the currently selected chat, add it + auto-mark read
                        if (isActiveChat) {
                            const messageId = normalizedMessage?.id;
                            const messageAlreadyLoaded = messageId
                                ? messagesRef.current.some(
                                    (m) => String(m.id) === String(messageId)
                                )
                                : false;
                            if (!messageAlreadyLoaded && messageId) {
                                adjustMessageTotal(chatIdOfMsg, 1);
                            }
                            setMessages((prev) => upsertMessage(prev, normalizedMessage));

                            // Immediately mark as read on the server so if we refresh,
                            // the server doesn't think we have unread.
                            if (String(normalizedMessage.senderId) !== String(currentUserId)) {
                                axios
                                    .post(
                                        `${CHAT_SERVICE_URL}/api/chat/${chatIdOfMsg}/mark-as-read/${currentUserId}`,
                                        {},
                                        { withCredentials: true }
                                    )
                                    .then(async () => {

                                    // Re-check the server for the actual updated unread count
                                    const res = await axios.get(
                                        `${CHAT_SERVICE_URL}/api/chat/${chatIdOfMsg}/unread-count/${currentUserId}`,
                                        { withCredentials: true }
                                    );
                                    const newUnread = res.data;

                                    setChats((prev) =>
                                        prev.map((c) =>
                                            c.id === chatIdOfMsg
                                                ? { ...c, unreadMessages: newUnread }
                                                : c
                                        )
                                    );
                                    })
                                    .catch((err) => {
                                        console.error("Error auto-marking messages as read:", err);
                                    });
                            }
                        }

                        updateTypingIndicators(chatIdOfMsg, normalizedMessage.senderId, false);
                        break;
                    }
                }
            }, buildAuthHeaders());

            subscriptionsRef.current[chatId] = sub;
        },
        [
            subscriptionsRef,
            setMessages,
            setChats,
            currentUserId,
            normalizeMessage,
            updateTypingIndicators,
            adjustMessageTotal,
            untrackHiddenMessage,
            fetchLatestVisiblePreview,
        ]
    );

    // 3b) Subscribe to user-specific events (CHAT_CREATED / NEW_MESSAGE / CHAT_DELETED for me)
    const subscribeToUserTopic = useCallback(
        (client, userId) => {
            const userTopicKey = `userTopic-${userId}`;
            if (subscriptionsRef.current[userTopicKey]) return;

            const sub = client.subscribe(`/topic/user/${userId}`, async (msg) => {
                if (!msg?.body) {
                    return;
                }
                let payload;
                try {
                    payload = JSON.parse(msg.body);
                } catch (error) {
                    console.warn("Failed to parse user-topic payload", error);
                    return;
                }
                switch (payload.type) {
                    case "CHAT_CREATED": {
                        try {
                            const newChatId = payload.chatId;
                            setChats((prev) => {
                                if (prev.some((c) => c.id === newChatId)) {
                                    return prev;
                                }
                                fetchChatsList();
                                return prev;
                            });
                        } catch (err) {
                            console.error("Error handling CHAT_CREATED:", err);
                        }
                        break;
                    }

                    case "NEW_MESSAGE": {
                        const messageChatId = normalizeSelectedChatId(payload.chatId);
                        if (!messageChatId) {
                            break;
                        }
                        const isLoadedChat = chatsRef.current.some(
                            (chat) => String(chat.id) === messageChatId
                        );
                        if (!isLoadedChat) {
                            fetchChatsList();
                        }
                        break;
                    }

                    case "CHAT_DELETED": {
                        const deletedChatId = payload.chatId;
                        setChats((prev) => prev.filter((c) => c.id !== deletedChatId));

                        if (selectedChatIdRef.current === deletedChatId) {
                            setSelectedChatId(null);
                            setMessages([]);
                        }
                        break;
                    }

                    case "FRIEND_REQUESTS_UPDATED": {
                        break;
                    }

                    default:
                        console.warn("Unknown user-topic event:", payload);
                }
            }, buildAuthHeaders());

            subscriptionsRef.current[userTopicKey] = sub;
        },
        [subscriptionsRef, fetchChatsList]
    );

    const clearSubscriptions = useCallback(() => {
        unsubscribeSubscriptions(subscriptionsRef.current);
        subscriptionsRef.current = {};
    }, []);

    // 4) Subscribe once connected and we know currentUserId
    useEffect(() => {
        if (!currentUserId || !stompClient || !isSocketConnected) return undefined;

        let cancelled = false;
        clearSubscriptions();

        subscribeToUserTopic(stompClient, currentUserId);

        fetchChatsList().then(() => {
            if (cancelled) {
                return;
            }
            if (selectedChatIdRef.current) {
                const chatId = selectedChatIdRef.current;

                loadLatestMessages(chatId)
                    .then(() =>
                        axios.post(
                            `${CHAT_SERVICE_URL}/api/chat/${chatId}/mark-as-read/${currentUserId}`,
                            {},
                            { withCredentials: true }
                        )
                    )
                    .then(async () => {
                        // Re-check actual unread from server
                        const res = await axios.get(
                            `${CHAT_SERVICE_URL}/api/chat/${chatId}/unread-count/${currentUserId}`,
                            { withCredentials: true }
                        );
                        const newUnread = res.data;
                        setChats((prev) =>
                            prev.map((c) =>
                                c.id === chatId ? { ...c, unreadMessages: newUnread } : c
                            )
                        );
                    })
                    .catch((err) => {
                        console.error("Error re-marking messages as read:", err);
                    });
            }
        });

        return () => {
            cancelled = true;
            clearSubscriptions();
        };
    }, [clearSubscriptions, currentUserId, fetchChatsList, isSocketConnected, loadLatestMessages, stompClient, subscribeToUserTopic]);

    // 5) Whenever `chats` changes, subscribe to new ones if connected
    useEffect(() => {
        if (!stompClient || !isSocketConnected) return;

        chats.forEach((chat) => {
            if (!subscriptionsRef.current[chat.id]) {
                subscribeToChat(stompClient, chat.id);
            }
        });
    }, [chats, isSocketConnected, stompClient, subscribeToChat]);

    // 6) When user selects a chat, load messages, mark read, etc.
    useEffect(() => {
        if (!selectedChatId || !currentUserId) {
            setMessages([]);
            setHasMoreMessages(false);
            setMessageCursor(null);
            setMessagesLoading(false);
            setOlderMessagesLoading(false);
            return;
        }

        const chatId = selectedChatId;
        let cancelled = false;

        setMessages([]);
        setHasMoreMessages(false);
        setMessageCursor(null);

        loadLatestMessages(chatId, { ignoreStale: true })
            .catch((error) => {
                if (!cancelled) {
                    console.error("❌ Error fetching messages:", error);
                }
            });

        const markAsRead = async () => {
            try {
                await axios.post(
                    `${CHAT_SERVICE_URL}/api/chat/${chatId}/mark-as-read/${currentUserId}`,
                    {},
                    { withCredentials: true }
                );
                if (cancelled || String(selectedChatIdRef.current ?? "") !== String(chatId)) {
                    return;
                }
                const res = await axios.get(
                    `${CHAT_SERVICE_URL}/api/chat/${chatId}/unread-count/${currentUserId}`,
                    { withCredentials: true }
                );
                if (cancelled || String(selectedChatIdRef.current ?? "") !== String(chatId)) {
                    return;
                }
                const newUnread = res.data;

                setChats((prevChats) =>
                    prevChats.map((c) =>
                        c.id === chatId ? { ...c, unreadMessages: newUnread } : c
                    )
                );
            } catch (error) {
                if (!cancelled) {
                    console.error("❌ Error marking messages as read:", error);
                }
            }
        };

        markAsRead();

        return () => {
            cancelled = true;
        };
    }, [selectedChatId, currentUserId, loadLatestMessages]);

    // 7) Fetch friends list once
    useEffect(() => {
        const fetchFriends = async () => {
            try {
                const acceptedFriends = await friendshipService.getAcceptedFriends();
                setFriends(acceptedFriends);
                setFilteredFriends(acceptedFriends);
                if (Array.isArray(acceptedFriends)) {
                    const friendIds = new Set();
                    acceptedFriends.forEach((friend) => {
                        const friendId = friend?.userId ?? friend?.id;
                        if (!friendId) {
                            return;
                        }
                        const friendName = resolveUserDisplayName(friend);
                        if (friendName) {
                            userDirectoryRef.current.set(String(friendId), {
                                name: friendName,
                                avatarUrl: friend?.avatarUrl || friend?.profilePicture || null,
                            });
                        }
                        friendIds.add(friendId);
                    });
                    if (friendIds.size > 0) {
                        preloadPresence(Array.from(friendIds));
                    }
                }
            } catch (error) {
                console.error("❌ Error fetching friends:", error);
            }
        };
        fetchFriends();
    }, [preloadPresence]);

    useEffect(() => () => {
        typingPublishRef.current.forEach((entry, chatId) => {
            if (entry.timeoutId) {
                clearTimeout(entry.timeoutId);
            }
            if (entry.state) {
                sendTypingStatus(chatId, false);
            }
        });
        typingPublishRef.current.clear();
        typingTimeoutsRef.current.forEach((timeoutId) => clearTimeout(timeoutId));
        typingTimeoutsRef.current.clear();
    }, [sendTypingStatus]);

    const selectedGroupUserIdList = useMemo(
        () => Array.from(selectedGroupUserIds),
        [selectedGroupUserIds]
    );

    const resetNewChatSelection = useCallback(() => {
        setNewChatMode("direct");
        setSelectedGroupUserIds(new Set());
        setGroupChatCreateError(null);
    }, []);

    const closeNewChatDropdown = useCallback(() => {
        setShowDropdown(false);
        setSearchTerm("");
        setFilteredFriends(friends);
        resetNewChatSelection();
    }, [friends, resetNewChatSelection]);

    const handleNewChatModeChange = useCallback((mode) => {
        setNewChatMode(mode === "group" ? "group" : "direct");
        setGroupChatCreateError(null);
        if (mode !== "group") {
            setSelectedGroupUserIds(new Set());
        }
    }, []);

    const toggleGroupUserSelection = useCallback((userId) => {
        const normalizedUserId = normalizeId(userId);
        if (!normalizedUserId) {
            return;
        }

        setGroupChatCreateError(null);
        setSelectedGroupUserIds((prev) => {
            const next = new Set(prev);
            if (next.has(normalizedUserId)) {
                next.delete(normalizedUserId);
                return next;
            }
            if (next.size >= GROUP_CHAT_SELECTED_USER_LIMIT) {
                setGroupChatCreateError(t("chatPage.newChatDialog.errors.limit", {
                    limit: GROUP_CHAT_TOTAL_PARTICIPANT_LIMIT,
                }));
                return prev;
            }
            next.add(normalizedUserId);
            return next;
        });
    }, [t]);

    // 8) Close friend-search dropdown if clicking outside
    useEffect(() => {
        if (!showDropdown) {
            return undefined;
        }

        const handleClickOutside = (event) => {
            if (
                dropdownRef.current?.contains(event.target) ||
                addButtonRef.current?.contains(event.target)
            ) {
                return;
            }
            closeNewChatDropdown();
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [closeNewChatDropdown, showDropdown]);

    // 9) Scroll to the selected chat item in the sidebar
    useEffect(() => {
        if (selectedChatId && chatRefs.current[selectedChatId]) {
            setTimeout(() => {
                const chatElement = chatRefs.current[selectedChatId];
                const sidebar = document.querySelector(".chat-sidebar");
                if (chatElement && sidebar) {
                    const chatTop = chatElement.offsetTop;
                    const offset = chatTop - 45;
                    sidebar.scrollTo({
                        top: offset,
                        behavior: "smooth",
                    });
                }
            }, 300);
        }
    }, [selectedChatId, chats]);

    // 10) Start chat with a friend (or open existing)
    const startChatWithFriend = async (friendId, friendUsername) => {
        const normalizedFriendId = normalizeId(friendId);
        if (!normalizedFriendId) {
            return;
        }

        closeNewChatDropdown();

        const existingChat = chats.find((chat) => {
            if (!isDirectChatContext(chat)) {
                return false;
            }
            const identity = resolveDirectChatIdentity({
                participantIds: chat.participantIds,
                currentUserId: currentUserIdString,
            });
            return normalizeId(identity.userId) === normalizedFriendId;
        });
        if (existingChat) {
            selectChat(existingChat.id);
            return;
        }

        try {
            const createdChat = await chatService.createDirectChat(normalizedFriendId);

            const isPersonal = Boolean(currentUserIdString && normalizedFriendId && normalizedFriendId === currentUserIdString);
            const resolvedFriendName = isPersonal ? selfDisplayName : friendUsername;
            const participantIds = normalizeParticipantIds(createdChat.participantIds);

            const newChat = {
                id: createdChat.id,
                contextType: CHAT_CONTEXT_TYPES.DIRECT,
                username: resolvedFriendName,
                participantIds: participantIds.length
                    ? participantIds
                    : [normalizedFriendId, currentUserIdString].map((value) => normalizeId(value)).filter(Boolean),
                latestMessage: NO_CHAT_PREVIEW,
                unreadMessages: 0,
                lastMessageTimestamp: null,
                avatarUserId: normalizedFriendId,
                isPersonal,
                presenceStatus: "offline",
                online: false,
                lastSeen: null,
            };

            if (normalizedFriendId) {
                userDirectoryRef.current.set(normalizedFriendId, {
                    name: resolvedFriendName,
                    avatarUrl: null,
                });
            }

            setChats((prev) => {
                if (prev.some((c) => c.id === newChat.id)) return prev;
                return [...prev, newChat];
            });
            if (normalizedFriendId) {
                preloadPresence([normalizedFriendId]);
            }

            selectChat(createdChat.id);
        } catch (error) {
            console.error(
                "❌ Error creating chat:",
                error.response ? error.response.data : error
            );
        }
    };

    const createSelectedGroupChat = useCallback(async () => {
        if (groupChatCreateBusy) {
            return;
        }
        if (selectedGroupUserIdList.length < GROUP_CHAT_MIN_SELECTED_USERS) {
            setGroupChatCreateError(t("chatPage.newChatDialog.errors.minimum", {
                count: GROUP_CHAT_MIN_SELECTED_USERS,
            }));
            return;
        }

        setGroupChatCreateBusy(true);
        setGroupChatCreateError(null);
        try {
            const createdChat = await chatService.createGroupChat(selectedGroupUserIdList);
            const existingChat = chatsRef.current.find((chat) => String(chat.id) === String(createdChat.id));
            const [restoredPreview, restoredUnreadMessages] = existingChat
                ? [null, 0]
                : await Promise.all([
                    fetchLatestVisiblePreview(createdChat.id),
                    fetchChatUnreadCount(createdChat.id),
                ]);
            const newChat = await buildGroupChatSummary(createdChat, {
                latestMessage: existingChat?.latestMessage ?? restoredPreview?.latestMessage ?? NO_CHAT_PREVIEW,
                lastMessageTimestamp: existingChat?.lastMessageTimestamp ?? restoredPreview?.lastMessageTimestamp ?? null,
                unreadMessages: existingChat?.unreadMessages ?? restoredUnreadMessages,
            });
            setChats((prev) => {
                const existingIndex = prev.findIndex((chat) => String(chat.id) === String(newChat.id));
                if (existingIndex === -1) {
                    return [...prev, newChat];
                }
                const next = [...prev];
                next[existingIndex] = mergeChatSummaryPreservingLoadedPreview(next[existingIndex], newChat);
                return next;
            });
            closeNewChatDropdown();
            selectChat(createdChat.id);
        } catch (error) {
            console.error("❌ Error creating group chat:", error.response ? error.response.data : error);
            const status = error?.response?.status;
            setGroupChatCreateError(
                status === 400
                    ? t("chatPage.newChatDialog.errors.invalidGroup")
                    : t("chatPage.newChatDialog.errors.createFailed")
            );
        } finally {
            setGroupChatCreateBusy(false);
        }
    }, [
        buildGroupChatSummary,
        closeNewChatDropdown,
        fetchChatUnreadCount,
        fetchLatestVisiblePreview,
        groupChatCreateBusy,
        selectedGroupUserIdList,
        selectChat,
        t,
    ]);

    // 11) Filter the friends list
    const handleSearchChange = (e) => {
        const value = e.target.value.trim();
        setSearchTerm(value);

        if (!value) {
            setFilteredFriends(friends);
        } else {
            setFilteredFriends(
                friends.filter((friend) => matchesUserIdentityQuery(friend, value))
            );
        }
    };

    const handleMessageDelete = (messageId) => {
        const deletedMsg = messages.find((m) => m.id === messageId);
        const chatId = deletedMsg?.chatId;
        if (messageId !== undefined && messageId !== null) {
            pendingDeletedMessageIdsRef.current.add(String(messageId));
        }
        if (messageId && chatId) {
            untrackHiddenMessage(messageId, chatId);
        }
        if (chatId && String(chatId) === String(selectedChatIdRef.current)) {
            adjustMessageTotal(chatId, -1);
        }
        setMessages((prev) => prev.filter((m) => m.id !== messageId));
        if (chatId) {
            fetchLatestVisiblePreview(chatId).then((preview) => {
                const nextContent = preview.latestMessage;
                const nextTimestamp = preview.lastMessageTimestamp;
                setChats((prevChats) =>
                    prevChats.map((chat) =>
                        chat.id === chatId
                            ? {
                                ...chat,
                                latestMessage: nextContent,
                                lastMessageTimestamp: nextTimestamp ?? chat.lastMessageTimestamp,
                            }
                            : chat
                    )
                );
            });
        }
    };

    useEffect(() => {
        if (!activeChat) {
            return;
        }
        const chatId = activeChat.id;
        const key = String(chatId);
        if (Number.isFinite(messageTotals[key])) {
            return;
        }
        let cancelled = false;
        refreshMessageTotal(chatId)
            .catch((error) => {
                if (!cancelled) {
                    console.error("Failed to hydrate chat message count", error);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [activeChat, messageTotals, refreshMessageTotal]);

    const handleMessageEdit = (updatedMessage) => {
        setMessages((prev) =>
            prev.map((m) =>
                m.id === updatedMessage.id
                    ? normalizeMessage({
                        ...m,
                        ...updatedMessage,
                        senderId: m.senderId,
                        seenBy: m.seenBy,
                        deliveryStatus: m.deliveryStatus,
                    })
                    : m
            )
        );
        fetchLatestVisiblePreview(updatedMessage.chatId).then((preview) => {
            const nextContent = preview.latestMessage;
            const nextTimestamp = preview.lastMessageTimestamp;
            setChats((prevChats) =>
                prevChats.map((chat) =>
                    chat.id === updatedMessage.chatId
                        ? {
                            ...chat,
                            latestMessage: nextContent,
                            lastMessageTimestamp: nextTimestamp ?? chat.lastMessageTimestamp,
                        }
                        : chat
                )
            );
        });
    };

    const handleRequestDelete = (chatId) => {
        setChatToDelete(chatId);
    };

    const panelMotionOffset = isMobileViewport ? 32 : 0;

    const threadsMotionProps = isMobileViewport
        ? {
            initial: { opacity: 0, x: -panelMotionOffset },
            animate: { opacity: 1, x: 0 },
            exit: { opacity: 0, x: -panelMotionOffset },
        }
        : {
            initial: false,
            animate: { opacity: 1, x: 0 },
        };

    const conversationMotionProps = isMobileViewport
        ? {
            initial: { opacity: 0, x: panelMotionOffset },
            animate: { opacity: 1, x: 0 },
            exit: { opacity: 0, x: panelMotionOffset },
        }
        : {
            initial: false,
            animate: { opacity: 1, x: 0 },
        };

    const renderThreadsPanel = () => {
        // New Chat button + filter chips. On mobile these are portaled into the
        // fixed app header; on desktop they render inside the threads panel.
        const newChatButton = (
            <button
                ref={addButtonRef}
                type="button"
                className="chat-page__new-chat"
                aria-label={t("chatPage.newChat")}
                onClick={() => {
                    if (showDropdown) {
                        closeNewChatDropdown();
                    } else {
                        setSearchTerm("");
                        setFilteredFriends(friends);
                        resetNewChatSelection();
                        setShowDropdown(true);
                    }
                }}
            >
                <span className="chat-page__new-chat-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24">
                        <path
                            fill="currentColor"
                            d="M12 5a1 1 0 0 1 1 1v5h5a1 1 0 0 1 0 2h-5v5a1 1 0 0 1-2 0v-5H6a1 1 0 0 1 0-2h5V6a1 1 0 0 1 1-1z"
                        />
                    </svg>
                </span>
                <span className="chat-page__new-chat-label">{t("chatPage.newChat")}</span>
            </button>
        );

        const filtersNav = (
            <nav className="chat-page__filters" aria-label={t("chatPage.threads.filtersAria")}>
                {threadFilters.map((filter) => {
                    const isActiveFilter = chatFilter === filter.id;
                    const IconComponent = filter.Icon;
                    return (
                        <InfoIcon key={filter.id} text={filter.label} className="chat-filter-chip__tooltip">
                            <button
                                type="button"
                                className={classNames(
                                    "chat-filter-chip",
                                    { "chat-filter-chip--active": isActiveFilter }
                                )}
                                onClick={() => handleFilterChange(filter.id)}
                                aria-pressed={isActiveFilter}
                                aria-label={filter.label}
                            >
                                {IconComponent ? (
                                    <>
                                        <IconComponent className="chat-filter-chip__icon" aria-hidden="true" focusable="false" />
                                        <span className="chat-filter-chip__label chat-filter-chip__label--sr">
                                            {filter.label}
                                        </span>
                                    </>
                                ) : (
                                    <span className="chat-filter-chip__label">{filter.label}</span>
                                )}
                                <span className="chat-filter-chip__badge">{filter.badge}</span>
                            </button>
                        </InfoIcon>
                    );
                })}
            </nav>
        );

        return (
        <motion.div
            key="threads-panel"
            className="chat-page__panel-wrapper chat-page__panel-wrapper--threads"
            layout
            {...threadsMotionProps}
            transition={{ duration: 0.28, ease: "easeOut" }}
        >
            {mobileHeaderSlot
                ? ReactDOM.createPortal(
                    <div className="chat-page__threads-mobile-controls">
                        {filtersNav}
                        {newChatButton}
                    </div>,
                    mobileHeaderSlot,
                )
                : null}
            <FrostedPanel className="chat-page__panel chat-page__panel--threads" tone="neutral" depth="lg" flat>
                <header className="chat-page__threads-header">
                    <div className="chat-page__threads-top">
                        <span className="chat-page__eyebrow">{t("chatPage.threads.eyebrow")}</span>
                        {!mobileHeaderSlot ? newChatButton : null}
                    </div>
                    <div className="chat-page__threads-stats" aria-live="polite">
                        <span className="chat-page__stat">
                            <span className="chat-page__stat-icon" aria-hidden="true">
                                <svg viewBox="0 0 24 24">
                                    <path
                                        fill="currentColor"
                                        d="M4 4h16a1 1 0 0 1 1 1v12.586a1 1 0 0 1-1.707.707L16 17H4a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1z"
                                        opacity="0.4"
                                    />
                                    <path
                                        fill="currentColor"
                                        d="M7 9h10a1 1 0 1 1 0 2H7a1 1 0 0 1 0-2zm0 4h7a1 1 0 1 1 0 2H7a1 1 0 0 1 0-2z"
                                    />
                                </svg>
                            </span>
                            <span className="chat-page__stat-value">{sortedChats.length}</span>
                            <span className="chat-page__stat-label">{t("chatPage.threads.stats.total")}</span>
                        </span>
                        <span className="chat-page__stat chat-page__stat--unread">
                            <span className="chat-page__stat-icon" aria-hidden="true">
                                <svg viewBox="0 0 24 24">
                                    <path
                                        fill="currentColor"
                                        d="M3 12a9 9 0 1 1 9 9 1 1 0 0 1 0-2 7 7 0 1 0-7-7 1 1 0 0 1-2 0z"
                                        opacity="0.4"
                                    />
                                    <path
                                        fill="currentColor"
                                        d="M12 7a1 1 0 0 1 1 1v3.382l1.553 1.553a1 1 0 0 1-1.414 1.414l-1.76-1.76A1 1 0 0 1 11 12V8a1 1 0 0 1 1-1z"
                                    />
                                </svg>
                            </span>
                            <span className="chat-page__stat-value">{unreadCount}</span>
                            <span className="chat-page__stat-label">{t("chatPage.threads.stats.unread")}</span>
                        </span>
                        <span className="chat-page__stat chat-page__stat--online">
                            <span className="chat-page__stat-icon" aria-hidden="true">
                                <svg viewBox="0 0 24 24">
                                    <path
                                        fill="currentColor"
                                        d="M12 3a5 5 0 0 1 4.894 4.058A5 5 0 0 1 19 12v5h1a1 1 0 0 1 0 2H4a1 1 0 0 1 0-2h1v-5a5 5 0 0 1 2.106-4.1A5 5 0 0 1 12 3z"
                                        opacity="0.4"
                                    />
                                    <path
                                        fill="currentColor"
                                        d="M12 5a3 3 0 0 0-2.828 2H14.83A3 3 0 0 0 12 5zM8 12v5h8v-5a3 3 0 1 0-8 0z"
                                    />
                                </svg>
                            </span>
                            <span className="chat-page__stat-value">{onlineCount}</span>
                            <span className="chat-page__stat-label">{t("chatPage.threads.stats.online")}</span>
                        </span>
                    </div>
                </header>

                <div className="chat-page__search">
                    <span className="chat-page__search-icon" aria-hidden="true">
                        <svg viewBox="0 0 24 24">
                            <path
                                fill="currentColor"
                                d="M15.5 14h-.79l-.28-.27a6 6 0 1 0-.71.71l.27.28h.79l4.5 4.49a1 1 0 0 0 1.42-1.42zm-4.5.5a4 4 0 1 1 4-4 4 4 0 0 1-4 4z"
                            />
                        </svg>
                    </span>
                    <input
                        type="search"
                        value={threadQuery}
                        onChange={handleThreadSearchChange}
                        placeholder={t("chatPage.threads.search.placeholder")}
                        aria-label={t("chatPage.threads.search.label")}
                    />
                    {threadQuery && (
                        <button
                            type="button"
                            className="chat-page__search-clear"
                            onClick={handleThreadSearchClear}
                            aria-label={t("chatPage.threads.search.clear")}
                        >
                            <svg viewBox="0 0 24 24" aria-hidden="true">
                                <path
                                    fill="currentColor"
                                    d="M7.05 7.05a1 1 0 0 1 1.414 0L12 10.586l3.536-3.535a1 1 0 1 1 1.415 1.414L13.414 12l3.537 3.536a1 1 0 0 1-1.415 1.414L12 13.414l-3.536 3.536a1 1 0 0 1-1.414-1.414L10.586 12 7.05 8.464a1 1 0 0 1 0-1.414z"
                                />
                            </svg>
                        </button>
                    )}
                </div>

                {!mobileHeaderSlot ? filtersNav : null}

                <div className="chat-page__list chat-sidebar">
                    <ScrollArea className="chat-sidebar__scroll" ariaLabel="Conversation list">
                        {showDropdown && (
                            <FriendSearchDropdown
                                dropdownRef={dropdownRef}
                                searchTerm={searchTerm}
                                handleSearchChange={handleSearchChange}
                                filteredFriends={filteredFriends}
                                startChatWithFriend={startChatWithFriend}
                                chatMode={newChatMode}
                                onChatModeChange={handleNewChatModeChange}
                                selectedGroupUserIds={selectedGroupUserIdList}
                                onToggleGroupUser={toggleGroupUserSelection}
                                onCreateGroupChat={createSelectedGroupChat}
                                groupCreateDisabled={selectedGroupUserIdList.length < GROUP_CHAT_MIN_SELECTED_USERS}
                                groupCreateBusy={groupChatCreateBusy}
                                groupSelectionLimit={GROUP_CHAT_SELECTED_USER_LIMIT}
                                groupError={groupChatCreateError}
                                onClose={closeNewChatDropdown}
                                closeLabel={t("chatPage.newChatDialog.close")}
                                resolvePresence={getStatus}
                            />
                        )}
                        <ChatList
                            chats={localizedVisibleChats}
                            selectedChatId={selectedChatId}
                            setSelectedChatId={selectChat}
                            onRequestDelete={handleRequestDelete}
                            chatRefs={chatRefs}
                            loading={chatsLoading}
                            pinnedChatIds={pinnedChatIds}
                            mutedChatIds={mutedChatIds}
                            onTogglePin={togglePinnedChat}
                            onToggleMute={toggleMutedChat}
                            typingIndicators={typingIndicators}
                            emptyMessage={threadEmptyMessage}
                        />
                    </ScrollArea>
                </div>
            </FrostedPanel>
        </motion.div>
        );
    };

    const renderConversationPanel = () => {
        // The conversation header (avatar, presence, title, actions). On mobile
        // it is portaled into the fixed app header instead of rendering inline,
        // so it sits in the header bar rather than clipping under it.
        const conversationHeaderEl = activeChat ? (
            <ChatConversationHeader
                className={mobileHeaderSlot ? "chat-conversation__header--in-mobile-header" : undefined}
                leading={(
                    <>
                        {isMobileViewport && (
                            <BackButton
                                iconOnly
                                onClick={handleMobileBack}
                                label={t("chatPage.mobileBack")}
                                className="chat-page__back-button--inline"
                            />
                        )}
                        <UserCardAvatar
                            {...activeChatAvatarProps}
                            username={activeChat.username}
                            alt={resolveChatAvatarAlt(activeChat)}
                            className="chat-conversation__avatar"
                            radius="50%"
                            status={activeChatAvatarStatus}
                        />
                    </>
                )}
                title={activeChat.username || "Unknown user"}
                titleBadge={conversationTitleBadge}
                tags={conversationTags}
                secondaryItems={conversationSecondaryItems}
                actions={conversationActions}
                titleAs="h2"
                presenceUserId={activeChatIsGroup ? undefined : activeChat.avatarUserId}
                presenceStatus={activeChatPresenceRecord?.status}
                presenceLastSeen={activeChatPresenceRecord?.lastSeen}
                presenceDisplay="expanded"
            />
        ) : null;

        return (
        <motion.div
            key="conversation-panel"
            className="chat-page__panel-wrapper chat-page__panel-wrapper--conversation"
            layout
            {...conversationMotionProps}
            transition={{ duration: 0.28, ease: "easeOut" }}
        >
            <FrostedPanel className="chat-page__panel chat-page__panel--conversation" tone="primary" depth="lg">
                {isMobileViewport && !activeChat && (
                    <button
                        type="button"
                        className="chat-page__back-button"
                        onClick={handleMobileBack}
                    >
                        <img
                            src={chatBackIcon}
                            alt=""
                            aria-hidden="true"
                            className="chat-page__back-icon"
                        />
                        <span>{t("chatPage.mobileBack")}</span>
                    </button>
                )}
                {activeChat ? (
                    <>
                        {mobileHeaderSlot
                            ? ReactDOM.createPortal(conversationHeaderEl, mobileHeaderSlot)
                            : conversationHeaderEl}

                        {showPinnedHint ? (
                            <div className="chat-pinned-hint" role="status">
                                <ChatFilterPinnedIcon className="chat-pinned-hint__icon" aria-hidden="true" focusable="false" />
                                <div className="chat-pinned-hint__copy">
                                    <p>{t("chatPage.pinned.title")}</p>
                                    <span>{t("chatPage.pinned.subtitle")}</span>
                                </div>
                                <button
                                    type="button"
                                    className="chat-pinned-hint__close"
                                    onClick={() => setShowPinnedHint(false)}
                                >
                                    {t("common.close")}
                                </button>
                            </div>
                        ) : null}

                        {chatSearchOpen ? (
                            <div className="chat-search-panel" role="search">
                                <form className="chat-search-panel__form" onSubmit={handleChatSearchSubmit}>
                                    <label className="chat-search-panel__sr" htmlFor="chat-search-input">
                                        {t("chatPage.search.label")}
                                    </label>
                                    <div className="chat-search-panel__input">
                                        <input
                                            id="chat-search-input"
                                            ref={chatSearchInputRef}
                                            type="search"
                                            value={chatSearchQuery}
                                            onChange={handleChatSearchQueryChange}
                                            placeholder={t("chatPage.search.placeholder")}
                                            autoComplete="off"
                                        />
                                        {chatSearchQuery ? (
                                            <button
                                                type="button"
                                                className="chat-search-panel__clear"
                                                onClick={handleChatSearchClear}
                                                aria-label={t("chatPage.search.clear")}
                                            >
                                                <svg viewBox="0 0 24 24" aria-hidden="true">
                                                    <path
                                                        fill="currentColor"
                                                        d="M7.05 7.05a1 1 0 0 1 1.414 0L12 10.586l3.536-3.535a1 1 0 1 1 1.415 1.414L13.414 12l3.537 3.536a1 1 0 0 1-1.415 1.414L12 13.414l-3.536 3.536a1 1 0 0 1-1.414-1.414L10.586 12 7.05 8.464a1 1 0 0 1 0-1.414z"
                                                    />
                                                </svg>
                                            </button>
                                        ) : null}
                                    </div>
                                    <button
                                        type="submit"
                                        className="chat-search-panel__submit"
                                        disabled={chatSearchLoading}
                                    >
                                        {chatSearchLoading ? t("chatPage.search.searching") : t("chatPage.search.cta")}
                                    </button>
                                </form>
                                {chatSearchError ? (
                                    <div className="chat-search-panel__error" role="alert">
                                        {chatSearchError}
                                    </div>
                                ) : null}
                                <div className="chat-search-panel__results" role="list">
                                    {chatSearchLoading || chatSearchNavigating ? (
                                        <div className="chat-search-panel__loading">
                                            <LoadingSpinner />
                                            <span>{t("chatPage.search.searching")}</span>
                                        </div>
                                    ) : null}
                                    {!chatSearchLoading
                                    && !chatSearchNavigating
                                    && chatSearchResults.length === 0
                                    && Boolean(clampSearchQuery(chatSearchQuery)) ? (
                                        <div className="chat-search-panel__empty">
                                            {t("chatPage.search.empty")}
                                        </div>
                                    ) : null}
                                    {chatSearchResults.map((result) => (
                                        <button
                                            key={result?.message?.id ?? result.snippet}
                                            type="button"
                                            className="chat-search-panel__result"
                                            onClick={() => handleChatSearchResultSelect(result.message.id)}
                                            role="listitem"
                                        >
                                            <span className="chat-search-panel__result-snippet">
                                                {result.snippet || toPlainText(result.message?.content)}
                                            </span>
                                            <span className="chat-search-panel__result-meta">
                                                {formatRelativeTime(
                                                    result?.message?.timestamp
                                                    ?? result?.message?.createdAt
                                                    ?? result?.message?.sentAt
                                                )}
                                            </span>
                                        </button>
                                    ))}
                                    {chatSearchHasMore && chatSearchResults.length > 0 && !chatSearchLoading ? (
                                        <div className="chat-search-panel__more">
                                            {t("chatPage.search.moreResults")}
                                        </div>
                                    ) : null}
                                </div>
                            </div>
                        ) : null}

                        <ChatMessages
                            ref={chatMessagesRef}
                            messages={messages}
                            currentUserId={currentUserId}
                            onReply={setReplyingTo}
                            onEditStart={setEditingMessage}
                            typingParticipants={typingParticipants}
                            typingUserIds={typingParticipants}
                            resolveUserName={resolveUserName}
                            useBubbleLayout
                            resolveMessageStatus={resolveMessageStatus}
                            isInitialLoading={messagesLoading}
                            hasMoreAbove={hasMoreMessages}
                            onLoadOlderMessages={loadOlderMessages}
                            isFetchingOlder={olderMessagesLoading}
                            conversationKey={selectedChatId}
                            onDeleteMessage={handleMessageDelete}
                            onDeleteForMe={hideMessageForCurrentUser}
                            onEditMessage={handleMessageEdit}
                            onTogglePinMessage={handlePinToggle}
                            pinnedMessageIds={Array.from(pinnedMessageIds)}
                            onReact={handleReactToMessage}
                            downloadingAttachments={Array.from(downloadingAttachments)}
                            onAttachmentDownload={handleAttachmentDownload}
                            onAttachmentDownloadComplete={handleAttachmentDownloadComplete}
                            stompClient={stompClient}
                            isSocketConnected={isSocketConnected}
                        />

                        <ChatComposer
                            key={
                                selectedChatId === undefined || selectedChatId === null
                                    ? "chat-composer-none"
                                    : `chat-composer-${selectedChatId}`
                            }
                            valueHtml={chatDraftHtml}
                            onChangeHtml={handleComposerDraftChange}
                            isSending={isComposerSending}
                            onSend={handleSendComposedMessage}
                            mentionUsers={composerMentionUsers}
                            mentionChannels={[]}
                            ariaLabel="Message composer"
                            placeholderTarget={activeChat.username || t("chatPage.anonymousUser")}
                            accessory={
                                <ChatComposerAccessory
                                    replyingTo={replyingTo}
                                    onCancelReply={() => setReplyingTo(null)}
                                    editingMessage={editingMessage}
                                    onCancelEdit={() => setEditingMessage(null)}
                                    resolveUserName={resolveUserName}
                                    currentUserId={currentUserId}
                                    currentUser={currentUser}
                                />
                            }
                        />
                        {composerError ? (
                            <div className="chat-conversation__composer-error" role="status">
                                {composerError}
                            </div>
                        ) : null}
                    </>
                ) : (
                    <div className="chat-conversation__empty" role="status" aria-live="polite">
                        <div className="chat-conversation__empty-visual" aria-hidden="true">
                            <svg viewBox="0 0 120 120">
                                <defs>
                                    <linearGradient id="chatEmptyGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                                        <stop offset="0%" stopColor="#68d1ff" stopOpacity="0.8" />
                                        <stop offset="100%" stopColor="#4f5dff" stopOpacity="0.5" />
                                    </linearGradient>
                                </defs>
                                <circle cx="60" cy="60" r="50" fill="url(#chatEmptyGradient)" opacity="0.25" />
                                <path
                                    fill="rgba(112,176,255,0.4)"
                                    d="M40 72a6 6 0 0 1-5.38-8.58l13-28.4A7 7 0 0 1 54.06 30h21.88a7 7 0 0 1 6.44 4.02l13 28.4A6 6 0 0 1 90 72z"
                                />
                                <circle cx="50" cy="48" r="4" fill="#c2e7ff" />
                                <circle cx="70" cy="48" r="4" fill="#c2e7ff" />
                                <path
                                    d="M45 58a10 10 0 0 1 9.9-10h14.2A10 10 0 0 1 79 58v12.5a2.5 2.5 0 0 1-3.82 2.1l-5.62-3.52H54.62l-5.8 3.64A2.5 2.5 0 0 1 45 70.2z"
                                    fill="rgba(132,208,255,0.25)"
                                />
                                <circle cx="50" cy="48" r="3" fill="#8cd6ff" />
                                <circle cx="70" cy="48" r="3" fill="#8cd6ff" />
                            </svg>
                        </div>
                        <h2>{t("chatPage.conversationEmpty.title")}</h2>
                        <p>{t("chatPage.conversationEmpty.subtitle")}</p>
                        <button
                            type="button"
                            className="chat-conversation__empty-cta"
                            onClick={() => {
                                setSearchTerm("");
                                setFilteredFriends(friends);
                                setShowDropdown(true);
                            }}
                        >
                            {t("chatPage.conversationEmpty.cta")}
                        </button>
                    </div>
                )}
            </FrostedPanel>
        </motion.div>
        );
    };

    const chatToDeleteDetails = chatToDelete
        ? chats.find((chat) => String(chat.id) === String(chatToDelete)) || null
        : null;

    return (
        <div className="chat-page" id="chatPage">
            <div aria-hidden="true" />
            <div className="chat-page__layout">
                {isMobileViewport ? (
                    <AnimatePresence initial={false} mode="wait">
                        {mobilePanel === "threads" && renderThreadsPanel()}
                        {mobilePanel === "conversation" && renderConversationPanel()}
                    </AnimatePresence>
                ) : (
                    <>
                        {renderThreadsPanel()}
                        {renderConversationPanel()}
                    </>
                )}
            </div>
            {chatToDelete &&
                ReactDOM.createPortal(
                    <PopupOverlay>
                        <DeleteChatPopup
                            chatId={chatToDelete}
                            closeModal={() => setChatToDelete(null)}
                            selectedChatId={selectedChatId}
                            setSelectedChatId={selectChat}
                            allowDeleteForAll={chatToDeleteDetails ? !isGroupChatContext(chatToDeleteDetails) : false}
                            onSuccess={(id) => setChats((prev) => prev.filter((c) => c.id !== id))}
                        />
                    </PopupOverlay>,
                    document.getElementById("portal")
                )}
        </div>
    );
}
